import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { supabase } from "../supabase";
import * as XLSX from "xlsx";

const C = {
    bg: "#ffffff",
    surface: "#f7f7f7",
    border: "#e0e0e0",
    text: "#000000",
    muted: "#333333",
    dim: "#777777",
    accent: "#52658F",
    cyan: "#2a6a8a",
    peach: "#ECDBC9",
    grey: "#CDCDCD",
    green: "#10b981",
};
const SYNE = "'Fraunces', serif";
const MONO = "'Plus Jakarta Sans', sans-serif";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";

const TRANSLATIONS = {
    en: {
        // Upload Screen
        newReconciliation: "Check My Numbers",
        uploadSubtitle: "Upload two files (CSV or Excel) — we'll find every difference instantly.",
        loadSample: "↓ Load sample data",
        runReconciliation: "Check now →",
        recentReports: "RECENT CHECKS",
        issues: "ISSUES",
        matchRate: "MATCH SCORE",
        gap: "DIFFERENCE",
        sourceA: "FILE 1",
        sourceB: "FILE 2",
        systemName: "System name (e.g. POS System)",
        uploadPrompt: "Click to upload CSV or Excel",
        rowsLoaded: "rows loaded",
        clickToReplace: "rows loaded — click to replace",
        multipleSheetsFound: "Multiple sheets found — select one:",
        dataQualityTitle: "⚠ We noticed a few things in your file",
        confirm: "Fix & continue →",

        // Smart Setup
        smartSetupTitle: "Quick Setup",
        smartSetupSubtitle: "DGB read your files. Answer 3 quick questions — we'll handle everything else automatically.",
        detectedColumns: "WHAT WE FOUND IN YOUR FILES",
        question1: "QUESTION 1 OF 3",
        question1Title: "What's in each row of your file?",
        question2: "QUESTION 2 OF 3",
        question2Title: "Which columns appear in both files?",
        question2Subtitle: "These are the columns DGB will use to match rows between your two files — like Order ID, Store Code, or Date.",
        question3: "QUESTION 3 OF 3",
        question3Title: "Which column has the number you want to compare?",
        question3Subtitle: "This is the value DGB will check for differences — like Total Sales or Transaction Value.",
        runSmartReconciliation: "Check now →",
        analysing: "Checking...",
        preSelected: "✓ We pre-selected columns based on matching names",
        selectColumn: "Select column...",
        differentGranularities: "⚡ YOUR FILES HAVE DIFFERENT LEVELS OF DETAIL",
        granularityExplain: "DGB will automatically combine",
        granularityExplain2: "rows up to",
        granularityExplain3: "level before comparing.",
        columnMismatch: "⚠ The number of columns selected doesn't match between files. Please check again.",

        // Row type options
        oneTransaction: "One transaction",
        oneTransactionDesc: "e.g. Order A111 with total value",
        oneProduct: "One product/item",
        oneProductDesc: "e.g. Meat Lovers from Order A111",
        onePeriod: "One day/period",
        onePeriodDesc: "All sales on March 1st",
        somethingElse: "Something else",
        somethingElseDesc: "I'll describe it",

        // Results Screen
        totalIssues: "Total Issues",
        salesGap: "Total Difference",
        matchRateLabel: "Match Score",
        missingRows: "Missing Rows",
        rowsNeedReview: "rows need review",
        notInOneSource: "not found in one file",
        ofRows: "of",
        rows: "rows",
        filter: "FILTER",
        exportPDF: "↓ Export PDF",
        allFilter: "All",
        mismatch: "Numbers differ",
        missingPOS: "Only in File 2",
        missingFin: "Only in File 1",
        match: "Match",
        duplicate: "Duplicate",
        aiAnalysis: "WHAT DOES THIS MEAN?",
        clickRowAnalysis: "Click any row to understand what happened.",
        tryAgain: "Try again",
        newReport: "← New Check",
        backToUpload: "← Back",
        backToHome: "← Back to home",
        logOut: "Log out",
        posGreater: "File 1 > File 2",
        finGreater: "File 2 > File 1",
        balanced: "balanced",
        salesDiff: "Difference",
        txnDiff: "Txn Diff",
        date: "Date",
        store: "Store",
        idName: "ID / Name",
        diff: "Difference",
        status: "Status",
        noRowsFound: "No rows found",
        // Granularity banner
        granularityBannerTitle: "⚡ YOUR FILES HAVE DIFFERENT LEVELS OF DETAIL",
        granularityBannerBody1: "is one row per",
        granularityBannerBody2: "is one row per",
        granularityBannerBody3: "DGB will automatically combine the more detailed file before comparing. No extra steps needed.",
        granularityBannerNote: "✓ Handled automatically",
    },
    id: {
        // Upload Screen
        newReconciliation: "Cek Angka Saya",
        uploadSubtitle: "Upload dua file (CSV atau Excel) — kami akan menemukan setiap perbedaan secara instan.",
        loadSample: "↓ Muat data contoh",
        runReconciliation: "Cek sekarang →",
        recentReports: "PENGECEKAN TERKINI",
        issues: "MASALAH",
        matchRate: "SKOR KECOCOKAN",
        gap: "PERBEDAAN",
        sourceA: "FILE 1",
        sourceB: "FILE 2",
        systemName: "Nama sistem (contoh: Sistem POS)",
        uploadPrompt: "Klik untuk upload CSV atau Excel",
        rowsLoaded: "baris dimuat",
        clickToReplace: "baris dimuat — klik untuk ganti",
        multipleSheetsFound: "Beberapa sheet ditemukan — pilih satu:",
        dataQualityTitle: "⚠ Kami menemukan beberapa hal dalam file kamu",
        confirm: "Perbaiki & lanjutkan →",

        // Smart Setup
        smartSetupTitle: "Pengaturan Cepat",
        smartSetupSubtitle: "DGB sudah membaca file kamu. Jawab 3 pertanyaan singkat — kami akan menangani sisanya secara otomatis.",
        detectedColumns: "APA YANG KAMI TEMUKAN DI FILE KAMU",
        question1: "PERTANYAAN 1 DARI 3",
        question1Title: "Apa yang ada di setiap baris file kamu?",
        question2: "PERTANYAAN 2 DARI 3",
        question2Title: "Kolom mana yang ada di kedua file?",
        question2Subtitle: "Kolom inilah yang akan DGB gunakan untuk mencocokkan baris antar file — seperti ID Order, Kode Toko, atau Tanggal.",
        question3: "PERTANYAAN 3 DARI 3",
        question3Title: "Kolom mana yang berisi angka yang ingin kamu bandingkan?",
        question3Subtitle: "Ini adalah nilai yang akan DGB periksa perbedaannya — seperti Total Penjualan atau Nilai Transaksi.",
        runSmartReconciliation: "Cek sekarang →",
        analysing: "Mengecek...",
        preSelected: "✓ Kami telah memilih kolom berdasarkan kesamaan nama",
        selectColumn: "Pilih kolom...",
        differentGranularities: "⚡ FILE KAMU MEMILIKI TINGKAT DETAIL YANG BERBEDA",
        granularityExplain: "DGB akan otomatis menggabungkan",
        granularityExplain2: "baris ke level",
        granularityExplain3: "sebelum membandingkan.",
        columnMismatch: "⚠ Jumlah kolom yang dipilih tidak sama untuk kedua file. Mohon cek kembali.",

        // Row type options
        oneTransaction: "Satu transaksi",
        oneTransactionDesc: "contoh: Order A111 dengan total nilai",
        oneProduct: "Satu produk/item",
        oneProductDesc: "contoh: Meat Lovers dari Order A111",
        onePeriod: "Satu hari/periode",
        onePeriodDesc: "Semua penjualan pada 1 Maret",
        somethingElse: "Lainnya",
        somethingElseDesc: "Saya akan mendeskripsikannya",

        // Results Screen
        totalIssues: "Total Masalah",
        salesGap: "Total Perbedaan",
        matchRateLabel: "Skor Kecocokan",
        missingRows: "Baris Hilang",
        rowsNeedReview: "baris perlu ditinjau",
        notInOneSource: "tidak ditemukan di salah satu file",
        ofRows: "dari",
        rows: "baris",
        filter: "FILTER",
        exportPDF: "↓ Ekspor PDF",
        allFilter: "Semua",
        mismatch: "Angka berbeda",
        missingPOS: "Hanya di File 2",
        missingFin: "Hanya di File 1",
        match: "Cocok",
        duplicate: "Duplikat",
        aiAnalysis: "APA ARTINYA INI?",
        clickRowAnalysis: "Klik baris mana saja untuk memahami apa yang terjadi.",
        tryAgain: "Coba lagi",
        newReport: "← Cek Baru",
        backToUpload: "← Kembali",
        backToHome: "← Kembali ke beranda",
        logOut: "Keluar",
        posGreater: "File 1 > File 2",
        finGreater: "File 2 > File 1",
        balanced: "seimbang",
        salesDiff: "Perbedaan",
        txnDiff: "Selisih Transaksi",
        date: "Tanggal",
        store: "Toko",
        idName: "ID / Nama",
        diff: "Perbedaan",
        status: "Status",
        noRowsFound: "Tidak ada baris ditemukan",
        // Granularity banner
        granularityBannerTitle: "⚡ FILE KAMU MEMILIKI TINGKAT DETAIL YANG BERBEDA",
        granularityBannerBody1: "berisi satu baris per",
        granularityBannerBody2: "berisi satu baris per",
        granularityBannerBody3: "DGB akan otomatis menggabungkan file yang lebih detail sebelum membandingkan. Tidak ada langkah tambahan.",
        granularityBannerNote: "✓ Ditangani otomatis",
    }
};

// ── File Parser (CSV + XLSX) ─────────────────────────────────
// eslint-disable-next-line no-unused-vars
function parseFile(buffer, filename) {
    const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
    return { sheetNames: workbook.SheetNames, workbook };
}

function parseSheet(workbook, sheetName) {
    const sheet = workbook.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, dateNF: "yyyy-mm-dd", defval: "" });

    let headerRowIndex = 0;
    for (let i = 0; i < raw.length; i++) {
        if (raw[i].some(cell => cell !== "" && cell !== null && cell !== undefined)) { headerRowIndex = i; break; }
    }

    const headers = raw[headerRowIndex].map(h => String(h || "").trim()).filter(h => h !== "");
    const rows = raw.slice(headerRowIndex + 1)
        .filter(row => row.some(cell => cell !== "" && cell !== null))
        .map(row => {
            const obj = {};
            headers.forEach((h, i) => {
                const val = String(row[i] || "").trim();
                // Normalize date columns on load
                const isDateCol = h.toLowerCase().includes("date") || h.toLowerCase().includes("tanggal") || h.toLowerCase().includes("waktu");
                obj[h] = isDateCol ? normalizeDate(val) : val;
            });
            return obj;
        });
    const rawRows = raw.slice(headerRowIndex + 1)
        .map(row => { const obj = {}; headers.forEach((h, i) => { obj[h] = String(row[i] || "").trim(); }); return obj; });

    return { headers, rows, rawRows };
}

// ── Reconciliation Engine ─────────────────────────────────
// eslint-disable-next-line no-unused-vars
function reconcile(posData, finData) {
    const posMap = {}, finMap = {};
    posData.rows.forEach(r => { posMap[(r.date || r.tanggal) + "_" + (r.store_id || r.kode_toko)] = r; });
    finData.rows.forEach(r => { finMap[(r.date || r.tanggal) + "_" + (r.store_id || r.kode_toko)] = r; });
    const allKeys = new Set([...Object.keys(posMap), ...Object.keys(finMap)]);
    const results = [];
    allKeys.forEach(key => {
        const pos = posMap[key], fin = finMap[key];
        const parts = key.split("_");
        const storeName = (pos && (pos.store_name || pos.nama_toko)) || (fin && (fin.store_name || fin.nama_toko)) || parts[1];
        const posSales = pos ? cleanNumeric(pos.gross_sales || pos.pendapatan_bersih) : null;
        const finSales = fin ? cleanNumeric(fin.pendapatan_bersih || fin.gross_sales) : null;
        const posTx = pos ? parseInt(pos.transactions || pos.jumlah_transaksi || 0) : null;
        const finTx = fin ? parseInt(fin.jumlah_transaksi || fin.transactions || 0) : null;
        const salesDiff = (posSales !== null && finSales !== null) ? posSales - finSales : null;
        const txDiff = (posTx !== null && finTx !== null) ? posTx - finTx : null;
        let status = "match";
        if (!pos) status = "missing_pos";
        else if (!fin) status = "missing_fin";
        else if (salesDiff !== 0 || txDiff !== 0) status = "mismatch";
        results.push({ key, date: parts[0], storeId: parts[1], storeName, posSales, finSales, salesDiff, posTx, finTx, txDiff, status, posRow: pos, finRow: fin });
    });
    return results.sort((a, b) => a.date.localeCompare(b.date) || a.storeId.localeCompare(b.storeId));
}

// ── Header Classifier ─────────────────────────────────
function classifyHeaders(headers, rows) {
    const sampleRows = rows.slice(0, 5);
    return headers.map(header => {
        const samples = sampleRows.map(r => r[header]).filter(v => v !== undefined && v !== "");
        const h = header.toLowerCase();
        if (h.includes("date") || h.includes("tanggal") || h.includes("time") || h.includes("waktu") || h.includes("period") || samples.some(v => !isNaN(Date.parse(v)) && /^\d{4}-\d{2}-\d{2}/.test(v)))
            return { header, type: "DATE", icon: "📅" };
        if (h.includes("_id") || h.includes("id") || h.includes("_no") || h.includes("code") || h.includes("kode") || h.includes("number") || h.includes("nomor"))
            return { header, type: "KEY", icon: "🔑" };
        if (h.includes("sales") || h.includes("amount") || h.includes("total") || h.includes("price") || h.includes("harga") || h.includes("pendapatan") || h.includes("revenue") || h.includes("nilai") || h.includes("gross") || h.includes("net") || (samples.length > 0 && samples.every(v => !isNaN(parseFloat(v)) && parseFloat(v) > 100)))
            return { header, type: "AMOUNT", icon: "💰" };
        if (h.includes("qty") || h.includes("quantity") || h.includes("count") || h.includes("jumlah") || h.includes("transactions") || h.includes("transaksi") || h.includes("volume"))
            return { header, type: "COUNT", icon: "🔢" };
        if (h.includes("name") || h.includes("nama") || h.includes("type") || h.includes("tipe") || h.includes("category") || h.includes("kategori") || h.includes("product") || h.includes("produk") || h.includes("sku") || h.includes("item") || h.includes("store") || h.includes("toko"))
            return { header, type: "CATEGORY", icon: "🏷️" };
        return { header, type: "OTHER", icon: "📋" };
    });
}

// ── Granularity Detector ─────────────────────────────────
// eslint-disable-next-line no-unused-vars
function detectGranularity(rows, keyColumns) {
    if (keyColumns.length === 0) return "unknown";
    const values = rows.map(r => r[keyColumns[0].header]);
    return new Set(values).size === values.length ? "summary" : "detail";
}

// ── Join Key Suggester ─────────────────────────────────
function suggestJoinKeys(posClassified, finClassified) {
    const posKeys = posClassified.filter(c => c.type === "KEY").map(c => c.header);
    const finKeys = finClassified.filter(c => c.type === "KEY").map(c => c.header);
    const exactMatches = posKeys.filter(k => finKeys.includes(k));
    if (exactMatches.length > 0) return exactMatches;
    const similarMatches = [];
    posKeys.forEach(pk => finKeys.forEach(fk => {
        const pkc = pk.toLowerCase().replace(/[_-]/g, ""), fkc = fk.toLowerCase().replace(/[_-]/g, "");
        if (pkc === fkc || pkc.includes(fkc) || fkc.includes(pkc)) similarMatches.push({ pos: pk, fin: fk });
    }));
    return similarMatches;
}

// ── Numeric Cleaner ─────────────────────────────────
// Handles: "Rp 1.250.000", "1,250,000.00", "(125000)", "1.250.000,50"
function cleanNumeric(val) {
    if (val === null || val === undefined || val === "") return 0;
    let s = String(val).trim();
    // Remove currency symbols and labels
    s = s.replace(/Rp\.?\s*/gi, "").replace(/IDR\.?\s*/gi, "").trim();
    // Remove parentheses (negative notation) — keep the minus
    const isNegative = s.startsWith("(") && s.endsWith(")");
    s = s.replace(/[()]/g, "");
    // Detect Indonesian format: dots as thousands, comma as decimal (e.g. 1.250.000,50)
    const indonesianFormat = /^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s);
    if (indonesianFormat) {
        s = s.replace(/\./g, "").replace(",", ".");
    } else {
        // Western format: commas as thousands, dot as decimal (e.g. 1,250,000.50)
        s = s.replace(/,/g, "");
    }
    const result = parseFloat(s) || 0;
    return isNegative ? -result : result;
}

// ── Date Normalizer ─────────────────────────────────
// Converts common Indonesian/Excel date formats to YYYY-MM-DD
const ID_MONTHS = { jan:1,feb:2,mar:3,apr:4,mei:5,jun:6,jul:7,agu:8,sep:9,okt:10,nov:11,des:12,may:5,aug:8,oct:10,dec:12 };
function normalizeDate(val) {
    if (!val) return val;
    const s = String(val).trim();
    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // YYYY-MM-DD with timestamp — truncate to date only
    const isoWithTime = s.match(/^(\d{4}-\d{2}-\d{2})[T\s]\d{2}:\d{2}/);
    if (isoWithTime) return isoWithTime[1];
    // DD/MM/YYYY with timestamp — e.g. "01/05/2026 16:12:00"
    const dmyWithTime = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+\d{2}:\d{2}/);
    if (dmyWithTime) return `${dmyWithTime[3]}-${dmyWithTime[2].padStart(2,"0")}-${dmyWithTime[1].padStart(2,"0")}`;
    // DD/MM/YYYY
    const dmy1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dmy1) return `${dmy1[3]}-${dmy1[2].padStart(2,"0")}-${dmy1[1].padStart(2,"0")}`;
    // DD/MM/YY — e.g. 1/3/24 → 2024-03-01
    const dmy2s = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
    if (dmy2s) return `20${dmy2s[3]}-${dmy2s[2].padStart(2,"0")}-${dmy2s[1].padStart(2,"0")}`;
    // DD-MM-YYYY
    const dmy2 = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmy2) return `${dmy2[3]}-${dmy2[2]}-${dmy2[1]}`;
    // YYYYMMDD
    const ymd = s.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
    // D/DD Month(ID/EN) YYYY — e.g. "1 Maret 2024", "15 Jan 2024"
    const dmy3 = s.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
    if (dmy3) {
        const m = ID_MONTHS[dmy3[2].toLowerCase().substring(0,3)];
        if (m) return `${dmy3[3]}-${String(m).padStart(2,"0")}-${dmy3[1].padStart(2,"0")}`;
    }
    // MMM-YY or MMM-YYYY — e.g. "Mar-24", "Mar-2024"
    const my = s.match(/^([A-Za-z]{3})-(\d{2,4})$/);
    if (my) {
        const m = ID_MONTHS[my[1].toLowerCase()];
        const y = my[2].length === 2 ? "20" + my[2] : my[2];
        if (m) return `${y}-${String(m).padStart(2,"0")}-01`;
    }
    // Excel serial number (integer between 40000–60000 covers 2009–2064)
    const serial = parseInt(s);
    if (!isNaN(serial) && serial > 40000 && serial < 60000) {
        const date = new Date((serial - 25569) * 86400 * 1000);
        return date.toISOString().substring(0, 10);
    }
    return s; // return as-is if nothing matched
}

function getStatusStyle(status, t) {
    if (status === "match")       return { color: "#10b981", bg: "#e6f7f0", label: t("match") };
    if (status === "mismatch")    return { color: "#f59e0b", bg: "#fff8e8", label: t("mismatch") };
    if (status === "duplicate")   return { color: "#f97316", bg: "#fff0e0", label: t("duplicate") };
    if (status === "missing_pos") return { color: "#ef4444", bg: "#fde8e8", label: t("missingPOS") };
    return                               { color: "#ef4444", bg: "#fde8e8", label: t("missingFin") };
}

// ── Google Sheets URL Parser ─────────────────────────────────
function extractSheetId(url) {
    const m = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return m ? m[1] : null;
}

// ── Upload Zone Component ─────────────────────────────────
function UploadZone({ label, name, setName, raw, setRaw, color, onDataReady, existingData, t }) {
    const fileRef = useRef();
    const [sheetNames, setSheetNames] = useState([]);
    const [selectedSheet, setSelectedSheet] = useState("");
    const [workbook, setWorkbook] = useState(null);
    const [quality, setQuality] = useState(null);
    const [acceptedFixes, setAcceptedFixes] = useState([]);
    const [fileName, setFileName] = useState("");
    const [sheetsUrl, setSheetsUrl] = useState("");
    const [sheetsLoading, setSheetsLoading] = useState(false);
    const [sheetsError, setSheetsError] = useState("");

    function processSheet(wb, sheetName, headers, rows) {
        const q = analyzeDataQuality(headers, rows);
        setQuality(q);
        setAcceptedFixes(q.suggestions.map(s => s.type));
        if (q.suggestions.length === 0) onDataReady({ headers, rows });
    }

    async function handleFile(e) {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);
        const buffer = await file.arrayBuffer();
        const { sheetNames: sheets, workbook: wb } = parseFile(buffer, file.name);
        setWorkbook(wb);
        setSheetNames(sheets);
        const firstSheet = sheets[0];
        setSelectedSheet(firstSheet);
        const { headers, rows, rawRows } = parseSheet(wb, firstSheet);
        setRaw(JSON.stringify({ headers, rows }));
        processSheet(wb, firstSheet, headers, rawRows);
    }

    async function handleSheetsImport() {
        const id = extractSheetId(sheetsUrl);
        if (!id) { setSheetsError("Couldn't find a spreadsheet ID in that URL. Make sure it's a Google Sheets link."); return; }
        setSheetsLoading(true); setSheetsError("");
        try {
            const csvUrl = `https://docs.google.com/spreadsheets/d/${id}/export?format=csv`;
            const res = await fetch(csvUrl);
            if (!res.ok) {
                if (res.status === 401 || res.status === 403) {
                    setSheetsError("This sheet is private. Share it as 'Anyone with the link can view', or export it as CSV and upload instead.");
                } else {
                    setSheetsError(`Failed to fetch sheet (${res.status}). Check the URL and try again.`);
                }
                setSheetsLoading(false); return;
            }
            const text = await res.text();
            // Parse CSV text
            const lines = text.trim().split("\n");
            const parseCSVLine = (line) => {
                const result = []; let current = ""; let inQuotes = false;
                for (let i = 0; i < line.length; i++) {
                    if (line[i] === '"') { inQuotes = !inQuotes; }
                    else if (line[i] === "," && !inQuotes) { result.push(current.trim()); current = ""; }
                    else { current += line[i]; }
                }
                result.push(current.trim()); return result;
            };
            const headers = parseCSVLine(lines[0]);
            const rows = lines.slice(1).filter(l => l.trim()).map(line => {
                const vals = parseCSVLine(line);
                const obj = {};
                headers.forEach((h, i) => {
                    const val = (vals[i] || "").trim();
                    const isDateCol = h.toLowerCase().includes("date") || h.toLowerCase().includes("tanggal") || h.toLowerCase().includes("waktu");
                    obj[h] = isDateCol ? normalizeDate(val) : val;
                });
                return obj;
            });
            setRaw(JSON.stringify({ headers, rows }));
            setFileName("Google Sheets");
            processSheet(null, null, headers, rows);
        } catch (err) {
            setSheetsError("Couldn't load the sheet. Make sure it's publicly accessible.");
        }
        setSheetsLoading(false);
    }

    function handleSheetChange(sheetName) {
        setSelectedSheet(sheetName);
        const { headers, rows, rawRows } = parseSheet(workbook, sheetName);
        setRaw(JSON.stringify({ headers, rows }));
        processSheet(workbook, sheetName, headers, rawRows);
    }

    function handleApplyFixes() {
        const parsed = JSON.parse(raw);
        const activeFixes = quality.suggestions.filter(s => {
            const key = s.type === "normalize_date_ambiguous" ? s.type + s.col : s.type;
            return acceptedFixes.includes(key);
        });
        const { headers, rows } = applyFixes(parsed.headers, parsed.rows, activeFixes);
        setRaw(JSON.stringify({ headers, rows }));
        onDataReady({ headers, rows });
        setQuality(null);
    }

    const rowCount = raw ? (() => { try { return JSON.parse(raw).rows?.length || 0; } catch { return 0; } })() : 0;

    return (
        <div style={{ background: C.surface, border: "1.5px solid " + C.border, borderRadius: 14, padding: "1.5rem", flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
                <span style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "0.85rem", color: C.muted, letterSpacing: "0.1rem" }}>{label}</span>
            </div>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t("systemName")}
                style={{ background: "#ffffff", border: "1.5px solid " + C.border, borderRadius: 8, padding: "0.6rem 1rem", color: C.text, fontFamily: MONO, fontSize: "0.85rem", width: "100%", outline: "none", marginBottom: "0.75rem", boxSizing: "border-box" }} />

            {/* File upload zone */}
            <div onClick={() => fileRef.current && fileRef.current.click()}
                style={{ border: "1.5px dashed " + (existingData ? "#10b981" : C.border), borderRadius: 12, padding: "2rem", textAlign: "center", cursor: "pointer", background: existingData ? "#e8f5ee" : "transparent" }}>
                <input type="file" accept=".csv,.xlsx,.xls" ref={fileRef} style={{ display: "none" }} onChange={handleFile} />
                <div style={{ fontSize: "1.5rem", marginBottom: "0.4rem" }}>{existingData ? "✓" : "📂"}</div>
                <div style={{ fontSize: "0.8rem", color: existingData ? "#10b981" : C.dim }}>
                    {existingData ? `${existingData.rows.length} ${t("clickToReplace")}` : fileName || t("uploadPrompt")}
                </div>
            </div>

            {/* Google Sheets import */}
            <div style={{ marginTop: "0.75rem" }}>
                <div style={{ fontSize: "0.7rem", color: C.dim, marginBottom: "0.4rem" }}>OR IMPORT FROM GOOGLE SHEETS</div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                        type="text"
                        value={sheetsUrl}
                        onChange={e => { setSheetsUrl(e.target.value); setSheetsError(""); }}
                        placeholder="Paste Google Sheets URL..."
                        style={{ flex: 1, background: "#ffffff", border: "1.5px solid " + C.border, borderRadius: 8, padding: "0.5rem 0.75rem", color: C.text, fontFamily: MONO, fontSize: "0.75rem", outline: "none" }}
                    />
                    <button
                        onClick={handleSheetsImport}
                        disabled={!sheetsUrl || sheetsLoading}
                        style={{ background: sheetsUrl && !sheetsLoading ? "#d4edda" : "#f0f7f2", border: "1.5px solid " + (sheetsUrl ? "#10b981" : C.border), borderRadius: 8, padding: "0.5rem 0.85rem", color: sheetsUrl ? "#059669" : C.dim, fontFamily: MONO, fontSize: "0.72rem", cursor: sheetsUrl ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
                        {sheetsLoading ? "Loading..." : "↓ Import"}
                    </button>
                </div>
                {sheetsError && (
                    <div style={{ marginTop: "0.5rem", fontSize: "0.72rem", color: "#ef4444", lineHeight: 1.5 }}>
                        ⚠ {sheetsError}
                        {sheetsError.includes("private") && (
                            <div style={{ marginTop: "0.35rem", color: C.dim }}>
                                💡 In Google Sheets: <strong style={{ color: C.muted }}>File → Share → Anyone with the link</strong>, then set to Viewer.
                            </div>
                        )}
                    </div>
                )}
                {!sheetsError && (
                    <div style={{ marginTop: "0.4rem", fontSize: "0.68rem", color: C.dim, lineHeight: 1.5 }}>
                        Works for public sheets. Private sheet?{" "}
                        <span style={{ color: C.muted }}>File → Download → CSV</span>, then upload above.
                    </div>
                )}
            </div>

            {sheetNames.length > 1 && (
                <div style={{ marginTop: "0.75rem" }}>
                    <div style={{ fontSize: "0.72rem", color: C.accent, marginBottom: "0.4rem" }}>📋 {t("multipleSheetsFound")}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                        {sheetNames.map(s => (
                            <button key={s} onClick={() => handleSheetChange(s)}
                                style={{ background: selectedSheet === s ? C.accent : "transparent", border: "1.5px solid " + (selectedSheet === s ? C.accent : C.border), borderRadius: 6, padding: "0.3rem 0.75rem", fontFamily: MONO, fontSize: "0.72rem", color: selectedSheet === s ? "white" : C.dim, cursor: "pointer" }}>
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {rowCount > 0 && <div style={{ fontSize: "0.7rem", color: C.accent, marginTop: "0.5rem" }}>✓ {rowCount} {t("rowsLoaded")}</div>}
            {quality && quality.issues.length > 0 && (
                <div style={{ marginTop: "0.75rem", background: "#fffbe8", border: "1px solid #f59e0b44", borderRadius: 8, padding: "0.75rem" }}>
                    <div style={{ fontSize: "0.7rem", color: "#b45309", fontFamily: SYNE, fontWeight: 700, marginBottom: "0.5rem" }}>{t("dataQualityTitle")}</div>
                    {quality.suggestions.map(s => (
                        <div key={s.type + s.col} style={{ marginBottom: "0.5rem" }}>
                            {s.type === "normalize_date_ambiguous" ? (() => {
                                const example = s.exampleVal || "";
                                const parts = example.split("/");
                                const opt1 = parts.length === 3 ? `${parseInt(parts[0])} ${["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(parts[1])]} (DD/MM)` : "DD/MM";
                                const opt2 = parts.length === 3 ? `${parseInt(parts[1])} ${["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(parts[0])]} (MM/DD)` : "MM/DD";
                                return (
                                    <div>
                                        <div style={{ fontSize: "0.75rem", color: "#f59e0b", marginBottom: "0.5rem" }}>
                                            ⚠ Date "{example}" is ambiguous — which is correct?
                                        </div>
                                        <div style={{ display: "flex", gap: "0.5rem" }}>
                                            <button onClick={() => {
                                                setAcceptedFixes([...acceptedFixes.filter(f => f !== s.type + s.col), s.type + s.col]);
                                            }}
                                                style={{ background: acceptedFixes.includes(s.type + s.col) ? "#10b981" : "transparent", border: "1px solid #10b981", borderRadius: 5, padding: "0.25rem 0.75rem", color: "#10b981", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                                                {opt1}
                                            </button>
                                            <button onClick={() => {
                                                const updated = quality.suggestions.map(q => q.col === s.col && q.type === "normalize_date_ambiguous" ? { ...q, detectedFormat: "MM/DD/YYYY" } : q);
                                                setQuality({ ...quality, suggestions: updated });
                                                setAcceptedFixes([...acceptedFixes.filter(f => f !== s.type + s.col), s.type + s.col]);
                                            }}
                                                style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 5, padding: "0.25rem 0.75rem", color: "#aaaaaa", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                                                {opt2}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })() : (
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    <input type="checkbox" checked={acceptedFixes.includes(s.type)}
                                        onChange={e => e.target.checked ? setAcceptedFixes([...acceptedFixes, s.type]) : setAcceptedFixes(acceptedFixes.filter(f => f !== s.type))} />
                                    <span style={{ fontSize: "0.75rem", color: C.muted }}>{s.message}</span>
                                </div>
                            )}
                        </div>
                    ))}
                    <button onClick={handleApplyFixes}
                        style={{ marginTop: "0.5rem", background: C.accent, color: "white", border: "none", borderRadius: 6, padding: "0.35rem 0.75rem", fontFamily: SYNE, fontWeight: 600, fontSize: "0.72rem", cursor: "pointer" }}>
                        {t("confirm")}
                    </button>
                </div>
            )}
        </div>
    );
}

// ── Data Quality Checker ─────────────────────────────────
function analyzeDataQuality(headers, rows) {
    const issues = [], suggestions = [];
    const dateCols = headers.filter(h => h.toLowerCase().includes("date") || h.toLowerCase().includes("tanggal"));
    dateCols.forEach(col => {
        const values = rows.map(r => r[col]).filter(Boolean);
        const formats = new Set();
        let hasSlashFormat = false;

        values.slice(0, 20).forEach(v => {
            if (/^\d{4}-\d{2}-\d{2}$/.test(v)) formats.add("YYYY-MM-DD");
            else if (/^\d{2}\/\d{2}\/\d{4}$/.test(v) || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(v)) {
                formats.add("DD/MM or MM/DD");
                hasSlashFormat = true;
            }
            else if (/^\d{1,2}\s+\w+\s+\d{4}$/.test(v)) formats.add("D MMM YYYY");
            else if (/^\d{2}-\d{2}-\d{4}$/.test(v)) formats.add("DD-MM-YYYY");
            else formats.add("unknown");
        });

        // Date ambiguity detection for slash formats
        if (hasSlashFormat) {
            const slashVals = values.slice(0, 50).filter(v => /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(v));
            let pos1Max = 0, pos2Max = 0;
            slashVals.forEach(v => {
                const parts = v.split("/");
                pos1Max = Math.max(pos1Max, parseInt(parts[0]));
                pos2Max = Math.max(pos2Max, parseInt(parts[1]));
            });

            if (pos1Max > 12) {
                suggestions.push({ type: "normalize_date", col, message: `Dates in "${col}" detected as DD/MM/YYYY (e.g. ${slashVals[0]}) — click to convert to YYYY-MM-DD`, auto: true, detectedFormat: "DD/MM/YYYY" });
            } else if (pos2Max > 12) {
                suggestions.push({ type: "normalize_date", col, message: `Dates in "${col}" detected as MM/DD/YYYY (e.g. ${slashVals[0]}) — click to convert to YYYY-MM-DD`, auto: true, detectedFormat: "MM/DD/YYYY" });
            } else {
                const exampleVal = slashVals[0] || "01/05/2026";
                suggestions.push({ type: "normalize_date_ambiguous", col, message: `Dates in "${col}" are ambiguous`, auto: false, detectedFormat: "DD/MM/YYYY", exampleVal });
            }
        }

        if (formats.size > 1 && !hasSlashFormat) {
            issues.push(`Mixed date formats in "${col}"`);
            suggestions.push({ type: "normalize_date", col, message: `Dates in "${col}" have different formats — click to standardize them`, auto: true });
        }
    });
    const blankRows = rows.filter((row, i) => i > 0 && i < rows.length - 1 && Object.values(row).every(v => v === ""));
    if (blankRows.length > 0) {
        issues.push(`${blankRows.length} blank row(s) found`);
        suggestions.push({ type: "remove_blank_rows", message: `${blankRows.length} blank row(s) found — click to remove them`, auto: true });
    }
    const headerCounts = {};
    headers.forEach(h => { headerCounts[h] = (headerCounts[h] || 0) + 1; });
    const dupHeaders = Object.entries(headerCounts).filter(([, c]) => c > 1).map(([h]) => h);
    if (dupHeaders.length > 0) {
        issues.push(`Duplicate column names: ${dupHeaders.join(", ")}`);
        suggestions.push({ type: "rename_duplicates", message: `Duplicate column names found — click to rename them automatically`, auto: true });
    }
    return { issues, suggestions };
}

// ── Apply Data Quality Fixes ─────────────────────────────────
function applyFixes(headers, rows, suggestions) {
    let fixedHeaders = [...headers], fixedRows = [...rows];
    suggestions.forEach(s => {
        if (s.type === "normalize_date" || s.type === "normalize_date_ambiguous") {
            const fmt = s.detectedFormat || "DD/MM/YYYY";
            fixedRows = fixedRows.map(row => {
                const val = row[s.col]; if (!val) return row;
                // DD/MM/YYYY → YYYY-MM-DD
                if (fmt === "DD/MM/YYYY") {
                    const m = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                    if (m) return { ...row, [s.col]: `${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}` };
                }
                // MM/DD/YYYY → YYYY-MM-DD
                if (fmt === "MM/DD/YYYY") {
                    const m = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                    if (m) return { ...row, [s.col]: `${m[3]}-${m[1].padStart(2,"0")}-${m[2].padStart(2,"0")}` };
                }
                // DD-MM-YYYY → YYYY-MM-DD
                const m2 = val.match(/^(\d{2})-(\d{2})-(\d{4})$/);
                if (m2) return { ...row, [s.col]: `${m2[3]}-${m2[2]}-${m2[1]}` };
                return row;
            });
        }
        if (s.type === "remove_blank_rows") fixedRows = fixedRows.filter(row => Object.values(row).some(v => v !== ""));
        if (s.type === "rename_duplicates") {
            const seen = {};
            fixedHeaders = fixedHeaders.map(h => { if (!seen[h]) { seen[h] = 0; return h; } seen[h]++; return `${h}_${seen[h]}`; });
        }
    });
    return { headers: fixedHeaders, rows: fixedRows };
}

// ── Industry Definitions (BLS NAICS-based) ─────────────────────────────────
const INDUSTRIES = [
    { id: "agriculture",   label: "Agriculture",                icon: "🌾",  naics: "11",               desc: "Farming, forestry, fishing, livestock" },
    { id: "automotive",    label: "Automotive",                 icon: "🚗",  naics: "441, 336",         desc: "Car dealers, repair, parts, manufacturing" },
    { id: "construction",  label: "Construction",               icon: "🏗️",  naics: "23",               desc: "Building, engineering, contractors" },
    { id: "ecommerce",     label: "E-commerce",                 icon: "🛒",  naics: "454",              desc: "Online retail, marketplaces, digital stores" },
    { id: "education",     label: "Education",                  icon: "🎓",  naics: "61",               desc: "Schools, universities, training providers" },
    { id: "energy",        label: "Energy & Utilities",         icon: "⚡",  naics: "22, 21",           desc: "Power, oil & gas, water, waste management" },
    { id: "finance",       label: "Finance & Banking",          icon: "🏦",  naics: "52",               desc: "Banks, credit, insurance, investments" },
    { id: "fnb",           label: "Food & Beverage",            icon: "🍽️",  naics: "722, 721",         desc: "Restaurants, cafes, catering, bars, hotels" },
    { id: "government",    label: "Government & Public Sector", icon: "🏛️",  naics: "92",               desc: "Public administration, agencies, NGOs" },
    { id: "healthcare",    label: "Health Care",                icon: "🏥",  naics: "62",               desc: "Hospitals, clinics, pharmacies, care facilities" },
    { id: "logistics",     label: "Logistics & Warehousing",    icon: "🚚",  naics: "48-49",            desc: "Freight, warehousing, couriers, shipping" },
    { id: "manufacturing", label: "Manufacturing",              icon: "🏭",  naics: "31-33",            desc: "Production, assembly, processing" },
    { id: "media",         label: "Media & Entertainment",      icon: "🎬",  naics: "51, 71",           desc: "Publishing, film, music, gaming, events" },
    { id: "professional",  label: "Professional Services",      icon: "💼",  naics: "54",               desc: "Consulting, legal, accounting, marketing" },
    { id: "realestate",    label: "Real Estate",                icon: "🏢",  naics: "53",               desc: "Property, rental, leasing" },
    { id: "retail",        label: "Retail",                     icon: "🛍️",  naics: "44-45",            desc: "Stores, supermarkets, specialty shops" },
    { id: "tech",          label: "Technology & Software",      icon: "💻",  naics: "518, 519",         desc: "SaaS, data services, IT, cloud" },
    { id: "telecom",       label: "Telecommunications",         icon: "📡",  naics: "517",              desc: "Mobile, internet, broadcasting providers" },
    { id: "travel",        label: "Travel & Hospitality",       icon: "✈️",  naics: "721, 481, 485, 487", desc: "Hotels, airlines, travel agencies, tour operators" },
    { id: "wholesale",     label: "Wholesale & Distribution",   icon: "📦",  naics: "42",               desc: "Wholesale trade, distributors, agents" },
    { id: "other",         label: "Other",                      icon: "🔧",  naics: null,               desc: "Something else not listed here" },
];

// ── Industry Auto-Detector ─────────────────────────────────
function detectIndustry(posHeaders, finHeaders) {
    const allCols = [...posHeaders, ...finHeaders].map(h => h.toLowerCase().replace(/[_\s-]/g, ""));

    const has = (...terms) => terms.some(t => allCols.some(c => c.includes(t)));

    const scores = {
        fnb:           has("outlet","menu","sku","cover","dinedin","takeaway","delivery","grabfood","gofood","cashier","shift","void") ? 3 : 0
                     + has("storecode","branchcode","restaurantid") ? 2 : 0,
        retail:        has("sku","productcode","itemcode","barcode","upc","pos","cashier") ? 2 : 0
                     + has("storeid","storecode","branchid") ? 1 : 0,
        ecommerce:     has("orderid","trackingnumber","marketplace","shopee","tokopedia","lazada","tiktokshop","bukalapak","platformfee","settlementid") ? 3 : 0,
        wholesale:     has("ponumber","purchaseorder","vendor","supplier","vendorid","deliverynote","goodsreceipt") ? 3 : 0,
        manufacturing: has("workorder","bom","bomid","materialcode","production","batch","lotno","machineid","shift") ? 3 : 0,
        finance:       has("accountno","accountnumber","loanid","transactionref","ledger","debit","credit","glcode","journalid","premium","policyno","interestrate","installment") ? 3 : 0,
        realestate:    has("propertyid","unitno","tenantid","lease","rent","occupancy","buildingcode") ? 3 : 0,
        logistics:     has("awb","airwaybill","shippingorder","waybill","manifest","truckid","driverid","routeid","pod","deliveryproof") ? 3 : 0,
        healthcare:    has("patientid","mrn","diagnosiscode","icd","ward","doctorno","prescriptionid","claimno") ? 3 : 0,
        tech:          has("subscriptionid","planid","userid","apikey","instanceid","featurecode","licenseid") ? 2 : 0,
        telecom:       has("msisdn","simcard","phoneno","datausage","callrecord","subscriberno","packagecode") ? 3 : 0,
        construction:  has("projectcode","contractno","boq","wbs","workpackage","sow","progressbilling","retention") ? 3 : 0,
        education:     has("studentid","enrollmentno","coursecode","semesterid","nip","nim","tuitionfee") ? 3 : 0,
        media:         has("contentid","episodeid","royalty","streamid","adid","campaignid","impressionid") ? 2 : 0,
        agriculture:   has("fieldid","cropid","harvestid","livestockid","farmcode","plotno","season") ? 3 : 0,
        energy:        has("meterid","electricityid","kwh","gasmeter","pln","waterusage","billingperiod","tapinstallation") ? 3 : 0,
        professional:  has("engagementid","clientcode","matterid","hourlyrate","timesheetid","billablehours","retainer") ? 2 : 0,
        government:    has("npwp","nip","anggaran","realisasi","dipa","rkakl","spm","sp2d","bast") ? 3 : 0,
        automotive:    has("vin","chassisno","plateno","servicejobno","sparepartcode","vehicleid","dealercode") ? 3 : 0,
        travel:        has("pnr","bookingreference","roomtype","checkin","checkout","flightno","guestid","reservationid","channelmanager","ota") ? 3 : 0,
    };

    const best = Object.entries(scores)
        .filter(([, s]) => s > 0)
        .sort(([, a], [, b]) => b - a);

    return best.length > 0 ? best[0][0] : null;
}
function SmartSetup({ posData, finData, posName, finName, onConfirm, t }) {
    const posClassified = classifyHeaders(posData.headers, posData.rows);
    const finClassified = classifyHeaders(finData.headers, finData.rows);
    const suggestedKeys = suggestJoinKeys(posClassified, finClassified);

    // Industry (optional, before Q1)
    const detectedIndustry = detectIndustry(posData.headers, finData.headers);
    const [industry, setIndustry] = useState(detectedIndustry || "");

    // Q1 — join key pairs: [{pos: "Tanggal", fin: "Tanggal"}, {pos: "Kode Outlet", fin: "Kode Gerai"}]
    const initPairs = () => {
        if (suggestedKeys.length === 0) return [{ pos: "", fin: "" }];
        return suggestedKeys.map(k => typeof k === "string" ? { pos: k, fin: k } : { pos: k.pos, fin: k.fin });
    };
    const [joinPairs, setJoinPairs] = useState(initPairs);

    // Derive joinKeys from pairs for downstream compatibility
    const joinKeys = {
        pos: joinPairs.filter(p => p.pos && p.fin).map(p => p.pos),
        fin: joinPairs.filter(p => p.pos && p.fin).map(p => p.fin),
    };

    // Q2 — uniqueness per file: "unique" = one row per key, "multiple" = many rows per key
    const [posUniqueness, setPosUniqueness] = useState("");
    const [finUniqueness, setFinUniqueness] = useState("");

    // Q3 — amount column (unchanged)
    // Smart amount column selection — prefer net sales over gross, gross amount over subtotal
    function pickAmountCol(classified) {
        const amounts = classified.filter(c => c.type === "AMOUNT").map(c => c.header);
        if (amounts.length === 0) return "";
        // Priority: net_sales > net > gross_amount > gross_sales > gross > total > first
        const priority = ["net sales", "net_sales", "netsales", "gross amount", "gross_amount", "grossamount", "gross sales", "gross_sales", "total collected", "total"];
        for (const p of priority) {
            const match = amounts.find(h => h.toLowerCase().replace(/[\s_]/g, "").includes(p.replace(/[\s_]/g, "")));
            if (match) return match;
        }
        return amounts[0];
    }

    const [amountCol, setAmountCol] = useState({
        pos: pickAmountCol(posClassified),
        fin: pickAmountCol(finClassified),
    });

    // Filters — per file, list of { column, type: "categorical"|"date", value, valueTo }
    const [posFilters, setPosFilters] = useState([]);
    const [finFilters, setFinFilters] = useState([]);

    // Helper: get unique values for a column (categorical)
    function getUniqueValues(rows, col) {
        return [...new Set(rows.map(r => r[col]).filter(Boolean))].sort();
    }

    // Helper: detect if a column is date-like
    function isDateColumn(header, rows) {
        const h = header.toLowerCase();
        // Must match date-like keyword in header name
        const nameMatch = h.includes("date") || h.includes("tanggal") || h.includes("waktu") || h.includes("jam") || (h.includes("time") && !h.includes("id"));
        if (!nameMatch) {
            // Or values must look like YYYY-MM-DD dates
            if (!rows) return false;
            const samples = rows.slice(0, 5).map(r => String(r[header] || ""));
            return samples.some(v => /^\d{4}-\d{2}-\d{2}/.test(v));
        }
        return true;
    }

    // Helper: detect if a column is categorical (≤30 unique values, not date, not amount)
    function isCategoricalColumn(header, rows) {
        if (isDateColumn(header, rows)) return false;
        const classified = classifyHeaders([header], rows).find(c => c.header === header);
        if (classified?.type === "AMOUNT" || classified?.type === "COUNT") return false;
        const unique = new Set(rows.map(r => r[header]).filter(Boolean));
        return unique.size <= 30 && unique.size >= 2;
    }

    // Helper: apply filters to rows
    function applyFilters(rows, filters) {
        return rows.filter(row => filters.every(f => {
            const val = row[f.column];
            if (!val) return true;
            if (f.type === "categorical") return val === f.value;
            if (f.type === "date") {
                const normalized = normalizeDate(val);
                if (f.value && f.valueTo) return normalized >= f.value && normalized <= f.valueTo;
                if (f.value) return normalized >= f.value;
                if (f.valueTo) return normalized <= f.valueTo;
            }
            return true;
        }));
    }

    // Filtered row counts for live preview
    const posFilteredCount = applyFilters(posData.rows, posFilters).length;
    const finFilteredCount = applyFilters(finData.rows, finFilters).length;

    // Categorical columns per file
    const posCategoricalCols = posData.headers.filter(h => isCategoricalColumn(h, posData.rows));
    const finCategoricalCols = finData.headers.filter(h => isCategoricalColumn(h, finData.rows));
    const posDateCols = posData.headers.filter(h => isDateColumn(h, posData.rows));
    const finDateCols = finData.headers.filter(h => isDateColumn(h, finData.rows));

    const [loading, setLoading] = useState(false);

    // Derive join key label for Q2 placeholder
    const joinKeyLabel = joinKeys.pos.length > 0 ? joinKeys.pos.join(" + ") : "the join key";

    // Map uniqueness answers to row types for the engine
    // "unique" → "transaction" (summary, no aggregation)
    // "multiple" → "product" (detail, needs aggregation)
    const uniquenessToRowType = (u) => u === "unique" ? "transaction" : "product";

    const selectStyle = { background: "#ffffff", border: "1.5px solid " + C.border, borderRadius: 8, padding: "0.5rem 0.75rem", color: C.text, fontFamily: MONO, fontSize: "0.85rem", width: "100%", outline: "none", cursor: "pointer" };

    const isComplete = joinKeys.pos.length > 0 && joinKeys.fin.length > 0 &&
        posUniqueness && finUniqueness &&
        amountCol.pos && amountCol.fin;

    function handleConfirm() {
        setLoading(true);
        onConfirm({
            posRowType: uniquenessToRowType(posUniqueness),
            finRowType: uniquenessToRowType(finUniqueness),
            joinKey: joinKeys,
            amountCol,
            industry,
            posClassified,
            finClassified,
            posFilters,
            finFilters,
        });
    }

    // Detect if granularity differs for the banner
    const granularityDiffers = posUniqueness && finUniqueness && posUniqueness !== finUniqueness;
    const detailSide = posUniqueness === "multiple" ? posName || t("sourceA") : finName || t("sourceB");
    const summarySide = posUniqueness === "unique" ? posName || t("sourceA") : finName || t("sourceB");

    return (
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "3rem 1.5rem", fontFamily: MONO }}>
            <div style={{ marginBottom: "2rem" }}>
                <h1 style={{ fontFamily: SYNE, fontSize: "1.8rem", fontWeight: 800, color: C.text, marginBottom: "0.5rem" }}>{t("smartSetupTitle")}</h1>
                <p style={{ color: C.muted }}>{t("smartSetupSubtitle")}</p>
            </div>

            {/* Detected columns */}
            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.25rem", marginBottom: "2rem" }}>
                <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "1rem" }}>{t("detectedColumns")}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                    {[{ name: posName || t("sourceA"), classified: posClassified, color: C.accent }, { name: finName || t("sourceB"), classified: finClassified, color: C.cyan }].map(source => (
                        <div key={source.name}>
                            <div style={{ fontSize: "0.75rem", color: source.color, fontWeight: 600, marginBottom: "0.5rem" }}>{source.name}</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                                {source.classified.map(col => (
                                    <span key={col.header} style={{ fontSize: "0.7rem", padding: "0.2rem 0.5rem", borderRadius: 4, background: C.bg, border: "1px solid " + C.border, color: C.muted }}>{col.icon} {col.header}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Industry selector — optional, before Q1 */}
            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.5rem", marginBottom: "1.25rem" }}>
                <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "1rem" }}>OPTIONAL — BEFORE WE START</div>
                <h3 style={{ color: C.text, fontFamily: SYNE, fontWeight: 700, marginBottom: "0.35rem" }}>What industry are you in?</h3>
                <p style={{ color: C.muted, fontSize: "0.82rem", marginBottom: detectedIndustry ? "0.75rem" : "1.25rem" }}>This helps DGB explain issues in language that makes sense for your business. You can skip this.</p>
                {detectedIndustry && (
                    <div style={{ marginBottom: "1.25rem", padding: "0.75rem 1rem", background: "#e8f0f8", border: "1px solid #06b6d444", borderRadius: 8, fontSize: "0.8rem", color: "#52658F" }}>
                        💡 Based on your column names, we think this might be <strong>{INDUSTRIES.find(i => i.id === detectedIndustry)?.label}</strong>. We've pre-selected it — change it if needed.
                    </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: "0.5rem" }}>
                    {INDUSTRIES.map(ind => (
                        <div key={ind.id} onClick={() => setIndustry(industry === ind.id ? "" : ind.id)}
                            style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", padding: "0.75rem", borderRadius: 8, border: "1.5px solid " + (industry === ind.id ? C.accent : C.border), background: industry === ind.id ? "#eef1f7" : C.bg, cursor: "pointer" }}>
                            <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>{ind.icon}</span>
                            <div>
                                <div style={{ fontSize: "0.78rem", color: C.text, fontWeight: industry === ind.id ? 600 : 400, lineHeight: 1.3 }}>{ind.label}</div>
                                <div style={{ fontSize: "0.65rem", color: C.dim, marginTop: "0.2rem", lineHeight: 1.4 }}>{ind.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>
                {industry && <div style={{ marginTop: "0.75rem", fontSize: "0.72rem", color: C.accent }}>✓ {industry === detectedIndustry ? "Auto-detected" : "Selected"}: {INDUSTRIES.find(i => i.id === industry)?.label}</div>}
            </div>

            {/* Q1 — Join key pairs */}
            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.5rem", marginBottom: "1.25rem" }}>
                <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "1rem" }}>QUESTION 1 OF 3</div>
                <h3 style={{ color: C.text, fontFamily: SYNE, fontWeight: 700, marginBottom: "0.5rem" }}>{t("question2Title")}</h3>
                <p style={{ color: C.muted, fontSize: "0.82rem", marginBottom: "1.25rem" }}>{t("question2Subtitle")}</p>

                {/* Column headers */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 32px 1fr 28px", gap: "0.5rem", marginBottom: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "0.72rem", color: C.accent, fontWeight: 600, paddingBottom: "0.4rem", borderBottom: "2px solid " + C.accent }}>
                        {posName || t("sourceA")}
                    </div>
                    <div />
                    <div style={{ fontSize: "0.72rem", color: C.cyan, fontWeight: 600, paddingBottom: "0.4rem", borderBottom: "2px solid " + C.cyan }}>
                        {finName || t("sourceB")}
                    </div>
                </div>

                {/* Pair rows */}
                {joinPairs.map((pair, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 32px 1fr 28px", gap: "0.5rem", marginBottom: "0.5rem", alignItems: "center" }}>
                        <select value={pair.pos} onChange={e => {
                            const newPairs = [...joinPairs];
                            newPairs[i] = { ...pair, pos: e.target.value };
                            setJoinPairs(newPairs);
                        }} style={{ ...selectStyle, border: "1.5px solid " + (pair.pos ? C.accent : C.border) }}>
                            <option value="">Select column...</option>
                            {posData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <div style={{ textAlign: "center", color: C.dim, fontSize: "0.85rem" }}>↔</div>
                        <select value={pair.fin} onChange={e => {
                            const newPairs = [...joinPairs];
                            newPairs[i] = { ...pair, fin: e.target.value };
                            setJoinPairs(newPairs);
                        }} style={{ ...selectStyle, border: "1.5px solid " + (pair.fin ? C.cyan : C.border) }}>
                            <option value="">Select column...</option>
                            {finData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                        {joinPairs.length > 1 && (
                            <button onClick={() => setJoinPairs(joinPairs.filter((_, j) => j !== i))}
                                style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 6, width: 28, height: 28, color: C.dim, cursor: "pointer", fontSize: "0.8rem" }}>✕</button>
                        )}
                    </div>
                ))}

                <button onClick={() => setJoinPairs([...joinPairs, { pos: "", fin: "" }])}
                    style={{ background: "transparent", border: "1px dashed " + C.border, borderRadius: 6, padding: "0.3rem 0.75rem", color: C.dim, fontFamily: MONO, fontSize: "0.72rem", cursor: "pointer", marginTop: "0.25rem" }}>
                    + Add another key
                </button>

                {suggestedKeys.length > 0 && <div style={{ marginTop: "0.75rem", fontSize: "0.75rem", color: "#059669" }}>{t("preSelected")}</div>}

                {/* Tip when date + store code both selected */}
                {joinKeys.pos.length > 1 && joinKeys.pos.some(k => k.toLowerCase().includes("tanggal") || k.toLowerCase().includes("date")) && (
                    <div style={{ marginTop: "0.75rem", padding: "0.75rem", background: "#e8f0f8", border: "1px solid #06b6d444", borderRadius: 8, fontSize: "0.8rem", color: "#52658F" }}>
                        💡 DGB will match rows where all selected columns are the same. This is perfect for daily reconciliation by store and date.
                    </div>
                )}
            </div>

            {/* Q2 — Uniqueness per file (replaces old row type question) */}
            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.5rem", marginBottom: "1.25rem" }}>
                <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "1rem" }}>QUESTION 2 OF 3</div>
                <h3 style={{ color: C.text, fontFamily: SYNE, fontWeight: 700, marginBottom: "0.5rem" }}>
                    In each file, can multiple rows share the same <em style={{ color: C.accent }}>{joinKeyLabel}</em>?
                </h3>
                <p style={{ color: C.muted, fontSize: "0.82rem", marginBottom: "1.25rem" }}>
                    This tells DGB whether it needs to add up rows before comparing.
                </p>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr", gap: "1rem" }}>

                    {/* File 1 */}
                    <div>
                        <div style={{ fontSize: "0.75rem", color: C.accent, fontWeight: 600, marginBottom: "0.75rem", paddingBottom: "0.4rem", borderBottom: "2px solid " + C.accent }}>
                            {posName || t("sourceA")}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            {[
                                { value: "unique", label: "No — each value appears exactly once", description: `e.g. one row per ${joinKeyLabel}` },
                                { value: "multiple", label: "Yes — multiple rows can share the same value", description: `e.g. several rows with the same ${joinKeyLabel} — DGB will add them up` },
                            ].map(opt => (
                                <div key={opt.value} onClick={() => setPosUniqueness(opt.value)}
                                    style={{ background: posUniqueness === opt.value ? "#eef1f7" : C.bg, border: "1.5px solid " + (posUniqueness === opt.value ? C.accent : C.border), borderRadius: 8, padding: "0.85rem", cursor: "pointer" }}>
                                    <div style={{ fontSize: "0.82rem", color: C.text, fontWeight: posUniqueness === opt.value ? 600 : 400 }}>{opt.label}</div>
                                    <div style={{ fontSize: "0.72rem", color: C.dim, marginTop: "0.25rem" }}>{opt.description}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ background: C.border }} />

                    {/* File 2 */}
                    <div>
                        <div style={{ fontSize: "0.75rem", color: C.cyan, fontWeight: 600, marginBottom: "0.75rem", paddingBottom: "0.4rem", borderBottom: "2px solid " + C.cyan }}>
                            {finName || t("sourceB")}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            {[
                                { value: "unique", label: "No — each value appears exactly once", description: `e.g. one row per ${joinKeyLabel}` },
                                { value: "multiple", label: "Yes — multiple rows can share the same value", description: `e.g. several rows with the same ${joinKeyLabel} — DGB will add them up` },
                            ].map(opt => (
                                <div key={opt.value} onClick={() => setFinUniqueness(opt.value)}
                                    style={{ background: finUniqueness === opt.value ? "#eef1f7" : C.bg, border: "1.5px solid " + (finUniqueness === opt.value ? C.cyan : C.border), borderRadius: 8, padding: "0.85rem", cursor: "pointer" }}>
                                    <div style={{ fontSize: "0.82rem", color: C.text, fontWeight: finUniqueness === opt.value ? 600 : 400 }}>{opt.label}</div>
                                    <div style={{ fontSize: "0.72rem", color: C.dim, marginTop: "0.25rem" }}>{opt.description}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                {/* Granularity banner — appears when one file has multiple rows per key */}
                {granularityDiffers && (
                    <div style={{ marginTop: "1rem", padding: "1.25rem", background: "#e8f5ee", border: "1px solid #10b98144", borderRadius: 10 }}>
                        <div style={{ fontSize: "0.7rem", color: "#10b981", fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.6rem" }}>
                            ⚡ DIFFERENT LEVELS OF DETAIL DETECTED
                        </div>
                        <div style={{ fontSize: "0.82rem", color: C.muted, lineHeight: 1.7, marginBottom: "0.5rem" }}>
                            <strong style={{ color: C.text }}>{detailSide}</strong> has multiple rows per <em>{joinKeyLabel}</em>.<br />
                            DGB will automatically add them up before comparing to <strong style={{ color: C.text }}>{summarySide}</strong>.
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#059669", fontWeight: 600 }}>✓ Handled automatically — no extra steps needed</div>
                    </div>
                )}
            </div>

            {/* Q3 — Amount column */}
            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.5rem", marginBottom: "2rem" }}>
                <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "1rem" }}>QUESTION 3 OF 3</div>
                <h3 style={{ color: C.text, fontFamily: SYNE, fontWeight: 700, marginBottom: "0.5rem" }}>{t("question3Title")}</h3>
                <p style={{ color: C.muted, fontSize: "0.82rem", marginBottom: "1.25rem" }}>{t("question3Subtitle")}</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr", gap: "1rem" }}>
                    <div>
                        <div style={{ fontSize: "0.75rem", color: C.accent, fontWeight: 600, marginBottom: "0.5rem", paddingBottom: "0.4rem", borderBottom: "2px solid " + C.accent }}>{posName || t("sourceA")}</div>
                        <select value={amountCol.pos} onChange={e => setAmountCol({ ...amountCol, pos: e.target.value })} style={selectStyle}>
                            <option value="">{t("selectColumn")}</option>
                            {posData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                    </div>
                    <div style={{ background: C.border }} />
                    <div>
                        <div style={{ fontSize: "0.75rem", color: C.cyan, fontWeight: 600, marginBottom: "0.5rem", paddingBottom: "0.4rem", borderBottom: "2px solid " + C.cyan }}>{finName || t("sourceB")}</div>
                        <select value={amountCol.fin} onChange={e => setAmountCol({ ...amountCol, fin: e.target.value })} style={selectStyle}>
                            <option value="">{t("selectColumn")}</option>
                            {finData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* Filter section */}
            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.5rem", marginBottom: "2rem" }}>
                <div style={{ fontSize: "0.7rem", color: C.muted, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.5rem" }}>OPTIONAL — FILTER YOUR DATA</div>
                <p style={{ color: C.dim, fontSize: "0.8rem", marginBottom: "1.25rem", lineHeight: 1.6 }}>
                    Narrow down what gets compared. Useful when one file contains multiple channels (e.g. Moka has GrabFood + Dine In) and you only want to compare one.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                    {[
                        { label: posName || t("sourceA"), color: C.accent, filters: posFilters, setFilters: setPosFilters, rows: posData.rows, headers: posData.headers, catCols: posCategoricalCols, dateCols: posDateCols, filteredCount: posFilteredCount, total: posData.rows.length },
                        { label: finName || t("sourceB"), color: C.cyan,   filters: finFilters, setFilters: setFinFilters, rows: finData.rows, headers: finData.headers, catCols: finCategoricalCols, dateCols: finDateCols, filteredCount: finFilteredCount, total: finData.rows.length },
                    ].map(src => (
                        <div key={src.label}>
                            <div style={{ fontSize: "0.75rem", color: src.color, fontWeight: 600, marginBottom: "0.75rem", paddingBottom: "0.4rem", borderBottom: "2px solid " + src.color }}>{src.label}</div>

                            {/* Existing filters */}
                            {src.filters.map((f, fi) => (
                                <div key={fi} style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 8, padding: "0.75rem", marginBottom: "0.5rem" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                        <span style={{ fontSize: "0.72rem", color: C.muted, fontWeight: 600 }}>{f.type === "categorical" ? "🏷 Category filter" : "📅 Date filter"}</span>
                                        <button onClick={() => src.setFilters(src.filters.filter((_, i) => i !== fi))}
                                            style={{ background: "transparent", border: "none", color: C.dim, cursor: "pointer", fontSize: "0.8rem" }}>✕</button>
                                    </div>
                                    {f.type === "categorical" && (
                                        <div style={{ display: "flex", gap: "0.5rem" }}>
                                            <select value={f.column} onChange={e => src.setFilters(src.filters.map((ff, i) => i === fi ? { ...ff, column: e.target.value, value: "" } : ff))}
                                                style={{ flex: 1, background: "#ffffff", border: "1px solid " + C.border, borderRadius: 6, padding: "0.35rem 0.5rem", color: C.text, fontFamily: MONO, fontSize: "0.78rem", outline: "none" }}>
                                                <option value="">Select column...</option>
                                                {src.catCols.map(h => <option key={h} value={h}>{h}</option>)}
                                            </select>
                                            {f.column && (
                                                <select value={f.value} onChange={e => src.setFilters(src.filters.map((ff, i) => i === fi ? { ...ff, value: e.target.value } : ff))}
                                                    style={{ flex: 1, background: "#ffffff", border: "1px solid " + C.border, borderRadius: 6, padding: "0.35rem 0.5rem", color: C.text, fontFamily: MONO, fontSize: "0.78rem", outline: "none" }}>
                                                    <option value="">Select value...</option>
                                                    {getUniqueValues(src.rows, f.column).map(v => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    )}
                                    {f.type === "date" && (
                                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                                            <select value={f.column} onChange={e => src.setFilters(src.filters.map((ff, i) => i === fi ? { ...ff, column: e.target.value } : ff))}
                                                style={{ flex: 1, minWidth: 120, background: "#ffffff", border: "1px solid " + C.border, borderRadius: 6, padding: "0.35rem 0.5rem", color: C.text, fontFamily: MONO, fontSize: "0.78rem", outline: "none" }}>
                                                <option value="">Select date column...</option>
                                                {src.dateCols.map(h => <option key={h} value={h}>{h}</option>)}
                                            </select>
                                            <input type="date" value={f.value || ""} onChange={e => src.setFilters(src.filters.map((ff, i) => i === fi ? { ...ff, value: e.target.value } : ff))}
                                                style={{ background: "#ffffff", border: "1px solid " + C.border, borderRadius: 6, padding: "0.35rem 0.5rem", color: C.text, fontFamily: MONO, fontSize: "0.78rem", outline: "none" }} />
                                            <span style={{ color: C.dim, fontSize: "0.75rem" }}>to</span>
                                            <input type="date" value={f.valueTo || ""} onChange={e => src.setFilters(src.filters.map((ff, i) => i === fi ? { ...ff, valueTo: e.target.value } : ff))}
                                                style={{ background: "#ffffff", border: "1px solid " + C.border, borderRadius: 6, padding: "0.35rem 0.5rem", color: C.text, fontFamily: MONO, fontSize: "0.78rem", outline: "none" }} />
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Add filter buttons */}
                            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
                                {src.catCols.length > 0 && (
                                    <button onClick={() => src.setFilters([...src.filters, { type: "categorical", column: "", value: "" }])}
                                        style={{ background: "transparent", border: "1px dashed " + C.border, borderRadius: 6, padding: "0.3rem 0.75rem", color: C.dim, fontFamily: MONO, fontSize: "0.72rem", cursor: "pointer" }}>
                                        + Category filter
                                    </button>
                                )}
                                {src.dateCols.length > 0 && (
                                    <button onClick={() => src.setFilters([...src.filters, { type: "date", column: src.dateCols[0], value: "", valueTo: "" }])}
                                        style={{ background: "transparent", border: "1px dashed " + C.border, borderRadius: 6, padding: "0.3rem 0.75rem", color: C.dim, fontFamily: MONO, fontSize: "0.72rem", cursor: "pointer" }}>
                                        + Date range
                                    </button>
                                )}
                            </div>

                            {/* Row count preview */}
                            <div style={{ fontSize: "0.72rem", color: src.filters.length > 0 ? src.color : C.dim }}>
                                {src.filters.length > 0
                                    ? `→ ${src.filteredCount.toLocaleString()} of ${src.total.toLocaleString()} rows will be used`
                                    : `All ${src.total.toLocaleString()} rows — no filter`}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <button disabled={!isComplete || loading} onClick={handleConfirm}
                style={{ width: "100%", background: isComplete ? C.accent : "#e0e0e0", color: isComplete ? "white" : "#555", border: "none", padding: "0.85rem 2rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 700, fontSize: "1rem", cursor: isComplete ? "pointer" : "not-allowed" }}>
                {loading ? t("analysing") : t("runSmartReconciliation")}
            </button>
        </div>
    );
}

// ── Upload Screen ─────────────────────────────────
function UploadScreen({ onRun, onDirectRun, userId, savedState, onSaveState, t, planError, planChecking, onClearPlanError }) {
    const navigate = useNavigate();
    const [posData, setPosData] = useState(savedState?.posData || null);
    const [finData, setFinData] = useState(savedState?.finData || null);
    const [posRaw, setPosRaw] = useState("");
    const [finRaw, setFinRaw] = useState("");
    const [posName, setPosName] = useState(savedState?.posName || "");
    const [finName, setFinName] = useState(savedState?.finName || "");
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);

    useEffect(() => {
        if (onSaveState && (posData || finData)) onSaveState({ posData, finData, posName, finName });
    }, [posData, finData, posName, finName, onSaveState]);

    const SAMPLE_ORDERS = `order_id,store_code,business_date,transaction_type,gross_sales
ORD-R001-0001,R001,2024-03-01,Dine-In,125000
ORD-R001-0002,R001,2024-03-01,Delivery,89000
ORD-R001-0003,R001,2024-03-01,Dine-In,135000
ORD-R001-0004,R001,2024-03-01,Takeaway,60000
ORD-R002-0001,R002,2024-03-01,Dine-In,75000
ORD-R002-0002,R002,2024-03-01,Delivery,160000
ORD-R001-0005,R001,2024-03-02,Dine-In,95000
ORD-R002-0003,R002,2024-03-01,Dine-In,80000`;

    const SAMPLE_ORDER_DETAILS = `order_id,store_code,business_date,sku,product_name,quantity,gross_sales
ORD-R001-0001,R001,2024-03-01,ML001,Meat Lovers Large,1,75000
ORD-R001-0001,R001,2024-03-01,GB001,Garlic Bread,2,25000
ORD-R001-0001,R001,2024-03-01,SD001,Soft Drink,1,25000
ORD-R001-0002,R001,2024-03-01,PP001,Pepperoni Personal,1,55000
ORD-R001-0002,R001,2024-03-01,SD001,Soft Drink,1,17000
ORD-R001-0002,R001,2024-03-01,SD001,Soft Drink,1,17000
ORD-R001-0003,R001,2024-03-01,ML001,Meat Lovers Large,2,75000
ORD-R001-0003,R001,2024-03-01,ML001,Meat Lovers Large,2,75000
ORD-R001-0004,R001,2024-03-01,CB001,Cheesy Bites,1,40000
ORD-R002-0001,R002,2024-03-01,PP001,Pepperoni Personal,1,75000
ORD-R002-0002,R002,2024-03-01,ML001,Meat Lovers Large,1,75000
ORD-R002-0002,R002,2024-03-01,GB001,Garlic Bread,1,25000
ORD-R002-0002,R002,2024-03-01,SD001,Soft Drink,2,60000
ORD-R001-0005,R001,2024-03-02,PP001,Pepperoni Personal,1,55000
ORD-R001-0005,R001,2024-03-02,SD001,Soft Drink,2,40000`;

    useEffect(() => {
        if (!userId) return;
        setHistoryLoading(true);
        supabase.from("reports").select("*").order("created_at", { ascending: false }).limit(5)
            .then(({ data, error }) => { if (!error && data) setHistory(data); setHistoryLoading(false); });
    }, [userId]);

    function parseSampleCSV(csv) {
        const lines = csv.trim().split("\n");
        const headers = lines[0].split(",");
        const rows = lines.slice(1).map(line => { const values = line.split(","); const obj = {}; headers.forEach((h, i) => { obj[h] = values[i] || ""; }); return obj; });
        return { headers, rows };
    }

    function loadSample() {
        const parsedPos = parseSampleCSV(SAMPLE_ORDERS);
        const parsedFin = parseSampleCSV(SAMPLE_ORDER_DETAILS);
        setPosData(parsedPos); setFinData(parsedFin);
        setPosName("Orders"); setFinName("Order Details");
        onDirectRun({ posData: parsedPos, finData: parsedFin, posName: "Orders", finName: "Order Details" });
    }

    return (
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "3rem 1.5rem", fontFamily: MONO }}>

            {planError === "limit_reached" ? (
                <div style={{ textAlign: "center", padding: "6rem 2rem" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🔒</div>
                    <div style={{ fontFamily: SYNE, fontSize: "1.5rem", fontWeight: 800, color: C.text, marginBottom: "0.75rem" }}>Monthly limit reached</div>
                    <div style={{ color: C.muted, fontSize: "0.9rem", lineHeight: 1.8, maxWidth: 420, margin: "0 auto 2rem" }}>
                        You've used all 3 free checks this month. Upgrade to Pro for unlimited checks, unlimited rows, and full AI analysis.
                    </div>
                    <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
                        <button onClick={() => navigate("/pricing")}
                            style={{ background: C.accent, color: "white", border: "none", padding: "0.85rem 2rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" }}>
                            See pricing →
                        </button>
                    </div>
                    <div style={{ marginTop: "1.5rem", fontSize: "0.78rem", color: C.dim }}>Limit resets at the start of next month.</div>
                </div>
            ) : (
                <>
                    {historyLoading ? (
                        <div style={{ marginBottom: "2.5rem" }}>
                            <h2 style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1rem", color: C.muted, letterSpacing: "0.1rem", marginBottom: "1rem" }}>{t("recentReports")}</h2>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                                {[1, 2, 3].map(i => <div key={i} style={{ height: 64, border: "1px solid " + C.border, borderRadius: 10, background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }} />)}
                            </div>
                        </div>
                    ) : history.length > 0 ? (
                        <div style={{ marginBottom: "2.5rem" }}>
                            <h2 style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1rem", color: C.muted, letterSpacing: "0.1rem", marginBottom: "1rem" }}>{t("recentReports")}</h2>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                                {history.map(report => (
                                    <div key={report.id} style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: "1rem 1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <div>
                                            <div style={{ fontSize: "0.85rem", color: C.text, fontWeight: 500 }}>{report.pos_name} vs {report.fin_name}</div>
                                            <div style={{ fontSize: "0.72rem", color: C.dim, marginTop: "0.25rem" }}>{new Date(report.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</div>
                                        </div>
                                        <div style={{ display: "flex", gap: "1.5rem", textAlign: "right" }}>
                                            <div><div style={{ fontSize: "0.7rem", color: C.dim }}>{t("issues")}</div><div style={{ fontSize: "0.9rem", color: report.issue_count > 0 ? "#f59d0b" : "#10b981", fontWeight: 600 }}>{report.issue_count}</div></div>
                                            <div><div style={{ fontSize: "0.7rem", color: C.dim }}>{t("matchRate")}</div><div style={{ fontSize: "0.9rem", color: C.accent, fontWeight: 600 }}>{Math.round((report.match_count / report.total_rows) * 100)}%</div></div>
                                            <div><div style={{ fontSize: "0.7rem", color: C.dim }}>{t("gap")}</div><div style={{ fontSize: "0.9rem", color: C.text, fontWeight: 600 }}>{Math.abs(report.total_diff).toLocaleString("id-ID")}</div></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div style={{ marginBottom: "2.5rem" }}>
                        <h1 style={{ fontFamily: SYNE, fontSize: "clamp(1.8rem,4vw,2.5rem)", fontWeight: 800, color: C.text, marginBottom: "0.75rem" }}>{t("newReconciliation")}</h1>
                        <p style={{ color: C.muted }}>{t("uploadSubtitle")}</p>
                    </div>

                    <div style={{ marginBottom: "1.5rem" }}>
                        <button onClick={loadSample} style={{ background: "transparent", color: C.accent, border: "1.5px solid " + C.accent, padding: "0.6rem 1.5rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 600, fontSize: "0.875rem", cursor: "pointer" }}>
                            {t("loadSample")}
                        </button>
                    </div>

                    <div style={{ display: "flex", gap: "1.25rem", marginBottom: "2rem", flexWrap: "wrap" }}>
                        <UploadZone label={t("sourceA")} name={posName} setName={setPosName} raw={posRaw} setRaw={setPosRaw} color={C.accent} onDataReady={setPosData} existingData={posData} t={t} />
                        <UploadZone label={t("sourceB")} name={finName} setName={setFinName} raw={finRaw} setRaw={setFinRaw} color={C.cyan} onDataReady={setFinData} existingData={finData} t={t} />
                    </div>

                    {planError === "row_limit" && (
                        <div style={{ marginBottom: "1rem", padding: "1.25rem 1.5rem", background: "#fde8e8", border: "1px solid #ef444466", borderRadius: 12 }}>
                            <div style={{ fontSize: "0.82rem", color: "#c0392b", fontWeight: 600, marginBottom: "0.35rem" }}>⚠ File too large for the free plan</div>
                            <div style={{ fontSize: "0.8rem", color: C.muted, marginBottom: "0.75rem" }}>Free accounts are limited to 100 rows per file. Upgrade to Pro for unlimited rows.</div>
                            <div style={{ display: "flex", gap: "0.75rem" }}>
                                <button onClick={() => navigate("/pricing")} style={{ background: C.accent, color: "white", border: "none", padding: "0.5rem 1.25rem", borderRadius: 7, fontFamily: SYNE, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer" }}>See pricing →</button>
                                <button onClick={onClearPlanError} style={{ background: "transparent", color: C.dim, border: "1px solid " + C.border, padding: "0.5rem 1rem", borderRadius: 7, fontFamily: MONO, fontSize: "0.78rem", cursor: "pointer" }}>Dismiss</button>
                            </div>
                        </div>
                    )}

                    <button disabled={!posData || !finData || planChecking} onClick={() => { if (!posData || !finData || planChecking) return; onRun({ posData, finData, posName, finName }); }}
                        style={{ width: "100%", background: posData && finData && !planChecking ? C.accent : "#e0e0e0", color: posData && finData && !planChecking ? "white" : "#555", border: "none", padding: "0.85rem 2rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 700, fontSize: "1rem", cursor: posData && finData && !planChecking ? "pointer" : "not-allowed" }}>
                        {planChecking ? "Checking..." : t("runReconciliation")}
                    </button>
                </>
            )}
        </div>
    );
}

// ── Summary Card ─────────────────────────────────
function SummaryCard({ label, value, color, sub }) {
    return (
        <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: "1.25rem", flex: 1 }}>
            <div style={{ fontSize: "0.65rem", color: C.dim, letterSpacing: "0.1em", marginBottom: "0.5rem", fontFamily: SYNE, fontWeight: 600 }}>{label.toUpperCase()}</div>
            <div style={{ fontSize: "1.4rem", fontWeight: 500, color, fontFamily: SYNE }}>{value}</div>
            <div style={{ fontSize: "0.7rem", color: C.dim, marginTop: "0.25rem" }}>{sub}</div>
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {[1, 2, 3].map(i => <div key={i} style={{ height: 16, borderRadius: 4, background: "linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 50%, #e8e8e8 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite", width: i === 3 ? "60%" : "100%" }} />)}
            <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
        </div>
    );
}

// ── Markdown Renderer ─────────────────────────────────
function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
        /^\*\*[^*]+\*\*$/.test(part)
            ? <strong key={i}>{part.slice(2, -2)}</strong>
            : part
    );
}

function renderMarkdown(text) {
    return text
        .split("\n")
        .map((line, i) => {
            if (/^### (.+)/.test(line))  return <h4 key={i} style={{ margin: "0.75rem 0 0.25rem", color: C.text, fontFamily: SYNE, fontSize: "0.85rem" }}>{line.replace(/^### /, "")}</h4>;
            if (/^## (.+)/.test(line))   return <h3 key={i} style={{ margin: "0.75rem 0 0.25rem", color: C.text, fontFamily: SYNE, fontSize: "0.9rem" }}>{line.replace(/^## /, "")}</h3>;
            if (/^- (.+)/.test(line))    return <div key={i} style={{ paddingLeft: "0.75rem", marginBottom: "0.25rem" }}>• {renderInline(line.replace(/^- /, ""))}</div>;
            if (line.trim() === "")      return <div key={i} style={{ height: "0.5rem" }} />;
            return <div key={i} style={{ marginBottom: "0.2rem" }}>{renderInline(line)}</div>;
        });
}

// ── Results Screen ─────────────────────────────────
function ResultsScreen({ results, posName, finName, joinKeys, industry, onRetryDateSwap, user, t, fingerprint, savedResolutionData, onClearSaved, lastConfig }) {
    const displayPosName = posName || t("sourceA");
    const displayFinName = finName || t("sourceB");
    const [selectedRow, _setSelectedRow] = useState(null);
    const [aiResponse, setAiResponse] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState(null);

    function setSelectedRow(row) {
        _setSelectedRow(row);
        if (row && explanations[row.key]) {
            setAiResponse(explanations[row.key]);
            setAiError(null);
        } else {
            setAiResponse("");
            setAiError(null);
        }
    }

    const [filter, setFilter] = useState("issues");

    const matchCount      = results.filter(r => r.status === "match").length;
    const mismatchCount   = results.filter(r => r.status === "mismatch").length;
    const missingPosCount = results.filter(r => r.status === "missing_pos").length;
    const missingFinCount = results.filter(r => r.status === "missing_fin").length;
    const duplicateCount  = results.filter(r => r.status === "duplicate").length;
    const issues          = mismatchCount + missingPosCount + missingFinCount + duplicateCount;
    const totalDiff       = results.reduce((s, r) => s + (r.salesDiff || 0), 0);
    const matchRate       = results.length ? Math.round((matchCount / results.length) * 100) : 0;

    // Filter: "issues" shows everything except match
    const filtered = results.filter(r =>
        filter === "all" ? true :
        filter === "issues" ? r.status !== "match" :
        r.status === filter
    );

    // ── Dynamic columns from join keys ──────────────────
    // One column per join key, directly from the key values
    const keys = joinKeys || [];
    const dynamicCols = keys.map((k, i) => ({
        key: k,
        label: k,
        width: "minmax(100px, 1fr)",
        render: r => r.joinKeyValues?.[i] || r.date || "—",
    }));
    // Fallback if joinKeyValues not present (old results)
    if (dynamicCols.length === 0) {
        dynamicCols.push({ key: "date", label: t("date"), width: "120px", render: r => r.date });
        dynamicCols.push({ key: "storeId", label: t("store"), width: "80px", render: r => r.storeId });
    }

    // Build grid template: dynamic cols + pos + fin + diff + status
    const fixedRight = "110px 110px 90px 100px";
    const dynamicWidths = dynamicCols.map(c => c.width).join(" ");
    const tableColumns = `${dynamicWidths} ${fixedRight}`;

    async function analyzeRow(row) {
        // If already explained this session, load from cache — no new API call
        if (explanations[row.key]) {
            setSelectedRow(row);
            setAiResponse(explanations[row.key]);
            setAiError(null);
            return;
        }
        setSelectedRow(row); setAiLoading(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + "/explain", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    store_name: row.storeName, date: row.date, store_id: row.storeId, status: row.status,
                    pos_sales: row.posSales, fin_sales: row.finSales, sales_diff: row.salesDiff,
                    pos_tx: row.posTx, fin_tx: row.finTx, tx_diff: row.txDiff,
                    pos_name: displayPosName, fin_name: displayFinName, has_duplicates: row.hasDuplicates,
                    industry: industry || null,
                    industry_label: industry ? (INDUSTRIES.find(i => i.id === industry)?.label || null) : null,
                    duplicate_context: row.hasDuplicates ? `One file has duplicate rows for this join key, causing the total to be inflated. The file with duplicates shows ${row.finSales?.toLocaleString()} while the other shows ${row.posSales?.toLocaleString()}.` : null,
                    user_id: user?.id || null,
                    granularity_differs: lastConfig?.config?.posRowType !== lastConfig?.config?.finRowType,
                    granularity_context: lastConfig?.config?.posRowType !== lastConfig?.config?.finRowType
                        ? `${displayPosName} is one row per ${lastConfig.config.posRowType}, while ${displayFinName} is one row per ${lastConfig.config.finRowType}. Row count differences between the two files are therefore expected and normal — do not flag them as suspicious.`
                        : null,
                })
            });
            if (response.status === 429) {
                setAiError("monthly_limit");
                setAiLoading(false); return;
            }
            if (!response.ok) { const e = await response.json(); throw new Error(e.detail || "Server error: " + response.status); }
            const data = await response.json();
            setAiResponse(data.explanation);
            // Cache for this session — won't fire again until page reload
            setExplanations(prev => ({ ...prev, [row.key]: data.explanation }));
        } catch (err) { setAiError(err.message); }
        setAiLoading(false);
    }

    const [includeMatches, setIncludeMatches] = useState(false);

    async function exportPDF() {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + "/export-pdf", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ pos_name: displayPosName, fin_name: displayFinName, total_rows: results.length, match_count: matchCount, issue_count: issues, total_diff: totalDiff, include_matches: includeMatches, rows: results.map(r => ({ date: r.date, store_id: r.storeId, store_name: r.storeName, pos_sales: r.posSales, fin_sales: r.finSales, sales_diff: r.salesDiff, pos_tx: r.posTx, fin_tx: r.finTx, status: r.status })) })
            });
            if (!response.ok) throw new Error("Export failed");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "dgb-report.pdf"; a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) { alert("Export failed: " + err.message); }
    }

    const posTotal = results.reduce((s, r) => s + (r.posSales ?? 0), 0);
    const finTotal = results.reduce((s, r) => s + (r.finSales ?? 0), 0);
    const totalGap = posTotal - finTotal;

    // ── Date format mismatch detection ──────────────────
    // Fires when: join keys include a date column AND match rate ≤ 10%
    const joinKeyList = joinKeys || [];
    const joinHasDate = joinKeyList.some(k =>
        k.toLowerCase().includes("date") || k.toLowerCase().includes("tanggal") || k.toLowerCase().includes("time")
    );
    const lowMatchRate = results.length >= 2 && matchRate <= 10;
    const likelyDateMismatch = joinHasDate && lowMatchRate;
    const [dateSwapDismissed, setDateSwapDismissed] = useState(false);
    const [resolutions, setResolutions] = useState({}); // key -> { type: "file1"|"file2"|"manual"|"investigate", value?: number }
    const [manualInputs, setManualInputs] = useState({}); // key -> string (temp input)
    const [showResumeBanner, setShowResumeBanner] = useState(!!savedResolutionData);
    const [explanations, setExplanations] = useState({}); // key -> explanation text, session-only

    // Auto-save resolutions to localStorage when they change
    useEffect(() => {
        if (!fingerprint || Object.keys(resolutions).length === 0) return;
        try {
            const issueRows = results.filter(r => r.status !== "match");
            localStorage.setItem(`dgb_resolutions_${fingerprint}`, JSON.stringify({
                resolutions,
                savedAt: new Date().toISOString(),
                resolvedCount: issueRows.filter(r => resolutions[r.key]).length,
                totalIssues: issueRows.length,
                posName, finName,
            }));
        } catch (e) { /* non-blocking */ }
    }, [resolutions, fingerprint, results, posName, finName]);

    // Resume saved resolutions
    function handleResume() {
        if (savedResolutionData?.resolutions) {
            // Only load resolutions for keys that still exist in current results
            const validKeys = new Set(results.map(r => r.key));
            const filtered = Object.fromEntries(
                Object.entries(savedResolutionData.resolutions).filter(([k]) => validKeys.has(k))
            );
            setResolutions(filtered);
        }
        setShowResumeBanner(false);
        onClearSaved?.();
    }

    function handleStartFresh() {
        if (fingerprint) localStorage.removeItem(`dgb_resolutions_${fingerprint}`);
        setShowResumeBanner(false);
        onClearSaved?.();
    }

    function setResolution(key, type, value) {
        setResolutions(prev => ({ ...prev, [key]: { type, value } }));
    }

    // Resolution progress
    const issueRows = results.filter(r => r.status !== "match");
    const resolvedCount = issueRows.filter(r => resolutions[r.key]).length;

    function downloadReconciled() {
        // Build output rows — all results with resolved values
        const rows = [...results].sort((a, b) => {
            // Sort by join key values
            const aKey = (a.joinKeyValues || [a.date, a.storeId]).join("_");
            const bKey = (b.joinKeyValues || [b.date, b.storeId]).join("_");
            return aKey.localeCompare(bKey);
        });

        const joinKeyCols = results[0]?.joinKeyCols || ["Date", "Store"];
        const headers = [...joinKeyCols, "Reconciled Value", "Status", "File 1 Value", "File 2 Value"];

        const csvRows = [headers.join(",")];
        rows.forEach(r => {
            const res = resolutions[r.key];
            let reconciledValue = "";
            let statusLabel = "";

            if (r.status === "match") {
                reconciledValue = r.posSales ?? r.finSales ?? "";
                statusLabel = "Match";
            } else if (res) {
                if (res.type === "file1") { reconciledValue = r.posSales ?? ""; statusLabel = `Resolved: ${displayPosName}`; }
                else if (res.type === "file2") { reconciledValue = r.finSales ?? ""; statusLabel = `Resolved: ${displayFinName}`; }
                else if (res.type === "manual") { reconciledValue = res.value ?? ""; statusLabel = "Resolved: Manual"; }
                else if (res.type === "investigate") { reconciledValue = ""; statusLabel = "Under Investigation"; }
            } else {
                reconciledValue = "";
                statusLabel = "Unresolved";
            }

            const keyVals = r.joinKeyValues || [r.date, r.storeId];
            const row = [
                ...keyVals.map(v => `"${v}"`),
                reconciledValue,
                `"${statusLabel}"`,
                r.posSales ?? "",
                r.finSales ?? "",
            ];
            csvRows.push(row.join(","));
        });

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "dgb-reconciled.csv"; a.click();
        URL.revokeObjectURL(url);
    }

    return (
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "1.5rem", fontFamily: MONO }}>
            <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                <SummaryCard label={t("totalIssues")}    value={String(issues)}   color={issues > 0 ? "#f50e0b" : "#10b981"} sub={t("rowsNeedReview")} />
                <SummaryCard label={t("salesGap")} value={Math.abs(totalGap).toLocaleString("id-ID")} color={totalGap !== 0 ? "#f59e0b" : "#10b981"} sub={totalGap > 0 ? t("posGreater") : totalGap < 0 ? t("finGreater") : t("balanced")} />
                <SummaryCard label={t("matchRateLabel")} value={matchRate + "%"}  color={C.accent} sub={matchCount + " of " + results.length + " rows"} />
                <SummaryCard label={t("missingRows")}    value={String(missingPosCount + missingFinCount)} color="#ef4444" sub={t("notInOneSource")} />
            </div>
            {/* File totals row */}
            <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
                <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: "0.85rem 1.25rem", flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.05em" }}>{displayPosName.toUpperCase()} TOTAL</div>
                    <div style={{ fontSize: "1.1rem", color: C.text, fontFamily: SYNE, fontWeight: 600 }}>{posTotal.toLocaleString("id-ID")}</div>
                </div>
                <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: "0.85rem 1.25rem", flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: "0.7rem", color: C.cyan, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.05em" }}>{displayFinName.toUpperCase()} TOTAL</div>
                    <div style={{ fontSize: "1.1rem", color: C.text, fontFamily: SYNE, fontWeight: 600 }}>{finTotal.toLocaleString("id-ID")}</div>
                </div>
            </div>

            {/* Resume banner */}
            {showResumeBanner && savedResolutionData && (
                <div style={{ marginBottom: "1rem", padding: "1rem 1.5rem", background: "#e8f0f8", border: "1px solid #06b6d444", borderRadius: 12, display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "0.75rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, marginBottom: "0.25rem" }}>
                            💾 SAVED PROGRESS FOUND
                        </div>
                        <div style={{ fontSize: "0.82rem", color: C.muted }}>
                            You resolved {savedResolutionData.resolvedCount} of {savedResolutionData.totalIssues} issues
                            {savedResolutionData.savedAt ? ` · Saved ${new Date(savedResolutionData.savedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button onClick={handleResume}
                            style={{ background: C.accent, color: "white", border: "none", borderRadius: 6, padding: "0.4rem 1rem", fontFamily: SYNE, fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" }}>
                            Resume →
                        </button>
                        <button onClick={handleStartFresh}
                            style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 6, padding: "0.4rem 0.75rem", color: C.dim, fontFamily: MONO, fontSize: "0.78rem", cursor: "pointer" }}>
                            Start fresh
                        </button>
                    </div>
                </div>
            )}

            {/* Date format mismatch warning */}
            {likelyDateMismatch && !dateSwapDismissed && (
                <div style={{ marginBottom: "1rem", padding: "1.25rem 1.5rem", background: "#fffbe8", border: "1px solid #f59e0b", borderRadius: 12, display: "flex", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 260 }}>
                        <div style={{ fontSize: "0.75rem", color: "#f59e0b", fontFamily: SYNE, fontWeight: 700, marginBottom: "0.35rem" }}>
                            ⚠ VERY LOW MATCH RATE — POSSIBLE DATE FORMAT MISMATCH
                        </div>
                        <div style={{ fontSize: "0.82rem", color: C.muted, lineHeight: 1.6 }}>
                            Only {matchRate}% of rows matched. This often happens when one file uses <strong style={{ color: C.text }}>DD/MM/YYYY</strong> and the other uses <strong style={{ color: C.text }}>MM/DD/YYYY</strong>. Try swapping the day and month in one of the files:
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                        <button onClick={() => onRetryDateSwap("pos")}
                            style={{ background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 6, padding: "0.4rem 0.85rem", color: "#f59e0b", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                            Swap dates in {displayPosName}
                        </button>
                        <button onClick={() => onRetryDateSwap("fin")}
                            style={{ background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 6, padding: "0.4rem 0.85rem", color: "#f59e0b", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                            Swap dates in {displayFinName}
                        </button>
                        <button onClick={() => onRetryDateSwap("both")}
                            style={{ background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 6, padding: "0.4rem 0.85rem", color: "#f59e0b", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                            Swap both
                        </button>
                        <button onClick={() => setDateSwapDismissed(true)}
                            style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 6, padding: "0.4rem 0.6rem", color: C.dim, fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                            ✕
                        </button>
                    </div>
                </div>
            )}

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, overflow: "auto", flex: 1 }}>
                    <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid " + C.border, display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ fontSize: "0.7rem", color: C.dim }}>{t("filter")}</span>
                            {[
                                { key: "issues",      label: `Issues only (${issues})`,              active: true },
                                { key: "all",         label: `${t("allFilter")} (${results.length})` },
                                { key: "mismatch",    label: `${t("mismatch")} (${mismatchCount})` },
                                { key: "missing_pos", label: `${t("missingPOS")} (${missingPosCount})` },
                                { key: "missing_fin", label: `${t("missingFin")} (${missingFinCount})` },
                                { key: "match",       label: `${t("match")} (${matchCount})` },
                                { key: "duplicate",   label: `${t("duplicate")} (${duplicateCount})` },
                            ].map(f => (
                                <button key={f.key} onClick={() => setFilter(f.key)}
                                    style={{ background: filter === f.key ? C.accent : "transparent", border: "1.5px solid " + (filter === f.key ? C.accent : C.border), borderRadius: 6, padding: "0.3rem 0.8rem", fontFamily: MONO, fontSize: "0.72rem", color: filter === f.key ? "white" : C.dim, cursor: "pointer" }}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                            {/* Resolution progress */}
                            {issueRows.length > 0 && (
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    <div style={{ fontSize: "0.72rem", color: resolvedCount === issueRows.length ? "#10b981" : C.dim }}>
                                        {resolvedCount}/{issueRows.length} resolved
                                    </div>
                                    <div style={{ width: 80, height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                                        <div style={{ width: `${(resolvedCount / issueRows.length) * 100}%`, height: "100%", background: resolvedCount === issueRows.length ? "#10b981" : C.accent, transition: "width 0.3s" }} />
                                    </div>
                                </div>
                            )}
                            <div onClick={() => setIncludeMatches(!includeMatches)}
                                style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", fontSize: "0.72rem", color: C.dim }}>
                                <div style={{ width: 28, height: 16, borderRadius: 8, background: includeMatches ? C.accent : C.border, position: "relative", transition: "background 0.2s" }}>
                                    <div style={{ position: "absolute", top: 2, left: includeMatches ? 14 : 2, width: 12, height: 12, borderRadius: "50%", background: "white", transition: "left 0.2s" }} />
                                </div>
                                Include matches
                            </div>
                            <button onClick={exportPDF} style={{ background: "transparent", border: "1.5px solid " + C.border, borderRadius: 6, padding: "0.3rem 0.8rem", fontFamily: MONO, fontSize: "0.72rem", color: C.muted, cursor: "pointer" }}>{t("exportPDF")}</button>
                            <button onClick={downloadReconciled}
                                style={{ background: resolvedCount > 0 ? "#10b981" : "transparent", border: "1.5px solid " + (resolvedCount > 0 ? "#10b981" : C.border), borderRadius: 6, padding: "0.3rem 0.8rem", fontFamily: MONO, fontSize: "0.72rem", color: resolvedCount > 0 ? "white" : C.dim, cursor: "pointer" }}>
                                ↓ Reconciled CSV
                            </button>
                        </div>
                    </div>

                    <div style={{ padding: "0.6rem 1.25rem", borderBottom: "1px solid " + C.border, background: "#e8f0f8", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <span style={{ fontSize: "0.75rem", color: C.accent }}>💡</span>
                        <span style={{ fontSize: "0.75rem", color: C.muted }}>Click any row to resolve it and get an AI explanation.</span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: 900 }}>
                            <div style={{ display: "grid", gridTemplateColumns: tableColumns, gap: "0.5rem", padding: "0.6rem 1.25rem", borderBottom: "1px solid " + C.border }}>
                                {[
                                    ...dynamicCols.map(c => typeof c.label === "string" ? c.label : c.key),
                                    displayPosName, displayFinName, t("diff"), t("status")
                                ].map(h => (
                                    <div key={h} style={{ fontSize: "0.62rem", color: C.dim, letterSpacing: "0.08rem", fontFamily: SYNE, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.toUpperCase()}</div>
                                ))}
                            </div>
                            <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
                                {filtered.map(row => {
                                    const s = getStatusStyle(row.status, t);
                                    const isSelected = selectedRow && selectedRow.key === row.key;
                                    const res = resolutions[row.key];
                                    const isIssue = row.status !== "match";
                                    return (
                                        <div key={row.key}>
                                            <div onClick={() => setSelectedRow(row === selectedRow ? null : row)}
                                                style={{ display: "grid", gridTemplateColumns: tableColumns, gap: "0.5rem", padding: "0.7rem 1.25rem", borderBottom: isSelected && isIssue ? "none" : "1px solid " + C.border, background: isSelected ? "#eef1f7" : res ? "#e8f5ee" : "transparent", borderLeft: isSelected ? "3px solid " + C.accent : res ? "3px solid #10b981" : "3px solid transparent", cursor: "pointer" }}>
                                                {dynamicCols.map(col => (
                                                    <div key={col.key} style={{ fontSize: col.bold ? "0.78rem" : "0.75rem", color: col.bold ? C.text : C.muted, fontWeight: col.bold ? 500 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {col.render(row) || "—"}
                                                    </div>
                                                ))}
                                                <div style={{ fontSize: "0.75rem", color: C.muted, whiteSpace: "nowrap" }}>{row.posSales != null ? (row.posSales / 1000).toFixed(0) + "k" : "-"}</div>
                                                <div style={{ fontSize: "0.75rem", color: C.muted, whiteSpace: "nowrap" }}>{row.finSales != null ? (row.finSales / 1000).toFixed(0) + "k" : "-"}</div>
                                                <div style={{ fontSize: "0.75rem", fontWeight: 500, whiteSpace: "nowrap", color: row.salesDiff === 0 ? "#10b981" : row.salesDiff > 0 ? "#f59e0b" : "#ef4444" }}>
                                                    {row.salesDiff == null ? "-" : row.salesDiff === 0 ? "✓" : (row.salesDiff > 0 ? "+" : "") + (Math.abs(row.salesDiff) / 1000).toFixed(0) + "k"}
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                                                    <span style={{ fontSize: "0.62rem", padding: "0.2rem 0.45rem", borderRadius: 4, background: s.bg, color: s.color, border: "1px solid " + s.color + "55" }}>{s.label}</span>
                                                    {res && <span style={{ fontSize: "0.6rem", color: "#10b981" }}>✓</span>}
                                                </div>
                                            </div>
                                            {/* Resolution panel — shows when row is selected and is an issue */}
                                            {isSelected && isIssue && (
                                                <div onClick={e => e.stopPropagation()} style={{ padding: "0.75rem 1.25rem", background: "#eef1f7", borderBottom: "1px solid " + C.border, borderLeft: "3px solid " + C.accent }}>
                                                    <div style={{ fontSize: "0.65rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.5rem" }}>RESOLVE THIS ROW</div>
                                                    {/* Line 1 — resolution choices */}
                                                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginBottom: "0.5rem" }}>
                                                        {row.posSales != null && (
                                                            <button onClick={() => setResolution(row.key, "file1", row.posSales)}
                                                                style={{ background: res?.type === "file1" ? C.accent : "transparent", border: "1px solid " + (res?.type === "file1" ? C.accent : C.border), borderRadius: 6, padding: "0.3rem 0.75rem", color: res?.type === "file1" ? "white" : C.muted, fontFamily: MONO, fontSize: "0.72rem", cursor: "pointer" }}>
                                                                Use {displayPosName} ({(row.posSales / 1000).toFixed(0)}k)
                                                            </button>
                                                        )}
                                                        {row.finSales != null && (
                                                            <button onClick={() => setResolution(row.key, "file2", row.finSales)}
                                                                style={{ background: res?.type === "file2" ? C.cyan : "transparent", border: "1px solid " + (res?.type === "file2" ? C.cyan : C.border), borderRadius: 6, padding: "0.3rem 0.75rem", color: res?.type === "file2" ? "white" : C.muted, fontFamily: MONO, fontSize: "0.72rem", cursor: "pointer" }}>
                                                                Use {displayFinName} ({(row.finSales / 1000).toFixed(0)}k)
                                                            </button>
                                                        )}
                                                        <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                                                            <input
                                                                type="number"
                                                                placeholder="Enter value"
                                                                value={manualInputs[row.key] || ""}
                                                                onChange={e => setManualInputs(prev => ({ ...prev, [row.key]: e.target.value }))}
                                                                style={{ background: "#ffffff", border: "1px solid " + (res?.type === "manual" ? "#10b981" : C.border), borderRadius: 6, padding: "0.3rem 0.5rem", color: C.text, fontFamily: MONO, fontSize: "0.72rem", width: 110, outline: "none" }}
                                                            />
                                                            <button onClick={() => { if (manualInputs[row.key]) setResolution(row.key, "manual", parseFloat(manualInputs[row.key])); }}
                                                                style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 6, padding: "0.3rem 0.5rem", color: C.muted, fontFamily: MONO, fontSize: "0.72rem", cursor: "pointer" }}>
                                                                Set
                                                            </button>
                                                        </div>
                                                        <button onClick={() => setResolution(row.key, "investigate")}
                                                            style={{ background: res?.type === "investigate" ? "#fff8e1" : "transparent", border: "1px solid " + (res?.type === "investigate" ? "#f59e0b" : C.border), borderRadius: 6, padding: "0.3rem 0.75rem", color: res?.type === "investigate" ? "#b45309" : C.dim, fontFamily: MONO, fontSize: "0.72rem", cursor: "pointer" }}>
                                                            Under investigation
                                                        </button>
                                                        {res && (
                                                            <button onClick={() => setResolutions(prev => { const n = {...prev}; delete n[row.key]; return n; })}
                                                                style={{ background: "transparent", border: "none", color: C.dim, fontFamily: MONO, fontSize: "0.72rem", cursor: "pointer" }}>
                                                                Clear
                                                            </button>
                                                        )}
                                                    </div>
                                                    {/* Line 2 — AI explanation, full width, prominent */}
                                                    <button
                                                        onClick={() => !explanations[row.key] && analyzeRow(row)}
                                                        disabled={!!explanations[row.key] || (aiLoading && selectedRow?.key === row.key)}
                                                        style={{ width: "100%", background: explanations[row.key] ? C.surface : "white", border: "1.5px solid " + (explanations[row.key] ? C.border : C.accent), borderRadius: 8, padding: "0.5rem 1rem", color: explanations[row.key] ? C.dim : C.accent, fontFamily: MONO, fontWeight: 600, fontSize: "0.8rem", cursor: explanations[row.key] ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }}>
                                                        {aiLoading && selectedRow?.key === row.key ? "Explaining..." : explanations[row.key] ? "✓ Explained — see panel on the right" : "✨ Explain this — what happened and what to do"}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <div style={{ padding: "3rem", textAlign: "center", color: C.dim, fontFamily: MONO }}>
                                        <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>✓</div>
                                        <div style={{ fontSize: "0.9rem" }}>{t("noRowsFound")}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {selectedRow && (
                    <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.25rem", width: "min(340px, 100%)", display: "flex", flexDirection: "column", gap: "1rem", maxHeight: "60vh", overflowY: "auto" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                                <div style={{ fontSize: "0.65rem", color: C.accent, letterSpacing: "0.12rem", fontFamily: SYNE, fontWeight: 700, marginBottom: "0.5rem" }}>{t("aiAnalysis")}</div>
                                <div style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1rem", color: C.text }}>{selectedRow.joinKeyValues ? selectedRow.joinKeyValues.join(" · ") : selectedRow.storeName}</div>
                                <div style={{ fontSize: "0.75rem", color: C.dim }}>{selectedRow.joinKeyCols ? selectedRow.joinKeyCols.join(" · ") : selectedRow.date + " · " + selectedRow.storeId}</div>
                            </div>
                            <button onClick={() => setSelectedRow(null)}
                                style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, cursor: "pointer", fontSize: "0.9rem", flexShrink: 0 }}>
                                ✕
                            </button>
                        </div>
                        <div style={{ background: C.surface, borderRadius: 8, padding: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                            {[
                                { label: displayPosName,  val: selectedRow.posSales != null ? selectedRow.posSales.toLocaleString("id-ID") : "-", color: C.accent },
                                { label: displayFinName,  val: selectedRow.finSales != null ? selectedRow.finSales.toLocaleString("id-ID") : "-", color: C.cyan },
                                { label: t("salesDiff"),  val: selectedRow.salesDiff === 0 ? "✓ Match" : selectedRow.salesDiff != null ? (selectedRow.salesDiff > 0 ? "+" : "") + Math.abs(selectedRow.salesDiff).toLocaleString("id-ID") : "—", color: selectedRow.salesDiff === 0 ? "#10b981" : "#f59e0b" },
                                { label: "Rows", val: selectedRow.posTx != null && selectedRow.finTx != null ? `${selectedRow.posTx} vs ${selectedRow.finTx} rows` : "—", color: C.muted },
                            ].map(item => (
                                <div key={item.label}>
                                    <div style={{ fontSize: "0.65rem", color: C.dim, marginBottom: "0.2rem" }}>{item.label}</div>
                                    <div style={{ fontSize: "0.82rem", color: item.color, fontWeight: 500 }}>{item.val}</div>
                                </div>
                            ))}
                        </div>
                        <div style={{ fontSize: "0.82rem", color: C.muted, lineHeight: 1.7 }}>
                            {aiLoading ? <LoadingSkeleton /> : aiError ? (
                                aiError === "monthly_limit" ? (
                                    <div style={{ fontSize: "0.82rem", lineHeight: 1.6 }}>
                                        <div style={{ color: "#f59e0b", fontWeight: 600, marginBottom: "0.5rem" }}>⚠ Monthly limit reached</div>
                                        <div style={{ color: C.muted }}>You've used all your AI explanations this month. Contact us to upgrade.</div>
                                        <a href="mailto:hello@datagetsbalanced.com?subject=DGB Pro Inquiry" style={{ display: "inline-block", marginTop: "0.75rem", color: C.accent, fontSize: "0.78rem" }}>hello@datagetsbalanced.com →</a>
                                    </div>
                                ) : (
                                <div style={{ color: "#ef4444", fontSize: "0.8rem" }}>
                                    ⚠ {aiError}
                                    <button onClick={() => analyzeRow(selectedRow)} style={{ display: "block", marginTop: "0.75rem", background: "transparent", border: "1px solid #ef4444", color: "#ef4444", padding: "0.4rem 0.75rem", borderRadius: 6, cursor: "pointer", fontSize: "0.75rem", fontFamily: MONO }}>{t("tryAgain")}</button>
                                </div>
                                )
                            ) : aiResponse ? (
                                <div style={{ fontSize: "0.82rem", color: C.muted, lineHeight: 1.7 }}>
                                    {renderMarkdown(aiResponse)}
                                </div>
                            ) : (
                                <span style={{ fontStyle: "italic", color: C.dim }}>{t("clickRowAnalysis")}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main AppPage ─────────────────────────────────
export default function AppPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [screen, setScreen] = useState("upload");

    // Inject fonts
    useEffect(() => {
        if (!document.querySelector('link[href*="Fraunces"]')) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = FONT_LINK;
            document.head.appendChild(link);
        }
    }, []);
    const [reportData, setReportData] = useState(null);
    const [uploadData, setUploadData] = useState(null);
    const [savedUpload, setSavedUpload] = useState(null);
    const [lastConfig, setLastConfig] = useState(null);
    const [currentFingerprint, setCurrentFingerprint] = useState(null);
    const [savedResolutionData, setSavedResolutionData] = useState(null);
    const [planError, setPlanError] = useState(() =>
        localStorage.getItem("dgb_plan_limited") === "1" ? "limit_reached" : null
    );
    const [planChecking, setPlanChecking] = useState(true);
    const [lang, setLang] = useState("en");

    const t = (key) => TRANSLATIONS[lang][key] || TRANSLATIONS["en"][key] || key;

    // ── Pre-check plan limit ──────────────────────────────────────
    async function checkPlanLimit() {
        if (!user?.id) return;
        setPlanChecking(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) { setPlanChecking(false); return; }

            const { data: prof, error } = await supabase
                .from("profiles")
                .select("plan")
                .eq("id", session.user.id)
                .single();

            if (error) { console.warn("[DGB] profiles error:", error.message); setPlanChecking(false); return; }

            const plan = prof?.plan || "free";
            console.log("[DGB] plan:", plan);

            if (plan !== "free") {
                localStorage.setItem("dgb_plan_limited", "0");
                setPlanError(null);
                setPlanChecking(false);
                return;
            }

            const now = new Date();
            const { data: reps, error: repsError } = await supabase
                .from("reports")
                .select("created_at")
                .eq("user_id", session.user.id);

            if (repsError) { console.warn("[DGB] reports error:", repsError.message); setPlanChecking(false); return; }

            const checksThisMonth = (reps || []).filter(r => {
                const d = new Date(r.created_at);
                return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            }).length;
            console.log("[DGB] checksThisMonth:", checksThisMonth);
            const limited = checksThisMonth >= 3;
            localStorage.setItem("dgb_plan_limited", limited ? "1" : "0");
            // If not limited, always clear the cache so previous month's limit doesn't persist
            if (!limited) localStorage.removeItem("dgb_plan_limited");
            setPlanError(limited ? "limit_reached" : null);
        } catch (e) { console.warn("[DGB] Plan limit check exception:", e); }
        setPlanChecking(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (user?.id) checkPlanLimit(); }, [user?.id]);
    // ── Apply filters from SmartSetup ──────────────────────
    function applySetupFilters(rows, filters) {
        if (!filters || filters.length === 0) return rows;
        return rows.filter(row => filters.every(f => {
            if (!f.column) return true;
            const val = row[f.column];
            if (!val) return true;
            if (f.type === "categorical") return !f.value || val === f.value;
            if (f.type === "date") {
                const normalized = normalizeDate(String(val));
                if (f.value && f.valueTo) return normalized >= f.value && normalized <= f.valueTo;
                if (f.value) return normalized >= f.value;
                if (f.valueTo) return normalized <= f.valueTo;
            }
            return true;
        }));
    }

    async function handleLogout() { await supabase.auth.signOut(); navigate("/"); }

    async function handleRun(data) {
        setReportData(data); setScreen("results");
        if (data.lastConfig && !lastConfig) setLastConfig(data.lastConfig);
        const { results } = data;
        const matchCount = results.filter(r => r.status === "match").length;
        const issueCount = results.filter(r => r.status !== "match").length;
        const totalDiff  = results.reduce((s, r) => s + (r.salesDiff || 0), 0);

        // Generate fingerprint for progress saving
        const posTotal = results.reduce((s, r) => s + (r.posSales ?? 0), 0);
        const joinKeyStr = (data.joinKeys || []).join("_");
        const fingerprint = `${data.posName}_${data.finName}_${results.length}_${Math.round(posTotal)}_${joinKeyStr}`;
        setCurrentFingerprint(fingerprint);

        // Check for saved resolutions
        try {
            const saved = localStorage.getItem(`dgb_resolutions_${fingerprint}`);
            if (saved) {
                const parsed = JSON.parse(saved);
                setSavedResolutionData(parsed);
            } else {
                setSavedResolutionData(null);
            }
        } catch (e) { /* non-blocking */ }

        const { error } = await supabase.from("reports").insert({ user_id: user.id, pos_name: data.posName, fin_name: data.finName, total_rows: results.length, match_count: matchCount, issue_count: issueCount, total_diff: totalDiff });
        if (error) console.error("Failed to save report:", error.message);
        checkPlanLimit();
    }

    // Retry reconciliation with day/month swapped in date columns
    async function handleRetryDateSwap(swapSide) {
        console.log("[DGB] handleRetryDateSwap called, swapSide:", swapSide, "lastConfig:", lastConfig);
        if (!lastConfig) { console.warn("[DGB] lastConfig is null — retry aborted"); return; }
        const { config, posRows, finRows, posName, finName } = lastConfig;
        console.log("[DGB] posRows sample:", posRows?.[0], "finRows sample:", finRows?.[0]);

        function swapDayMonth(rows) {
            const firstRow = rows[0] || {};
            const dateCols = Object.keys(firstRow).filter(k => {
                const kl = k.toLowerCase();
                return kl.includes("date") || kl.includes("tanggal") || kl.includes("waktu") || kl.includes("time");
            });
            if (dateCols.length === 0) return rows;

            return rows.map(row => {
                const newRow = { ...row };
                dateCols.forEach(col => {
                    const v = row[col];
                    if (!v) return;
                    let swapped = null;

                    // Only swap slash-format dates — never swap ISO (YYYY-MM-DD) dates
                    // as they are already unambiguous
                    const mdy = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
                    if (mdy) { swapped = `${mdy[2]}/${mdy[1]}/${mdy[3]}`; }

                    // DD-MM-YYYY ↔ MM-DD-YYYY (dash format without year first)
                    const dmy = v.match(/^(\d{2})-(\d{2})-(\d{4})$/);
                    if (dmy) { swapped = `${dmy[2]}-${dmy[1]}-${dmy[3]}`; }

                    if (swapped) newRow[col] = normalizeDate(swapped);
                });
                return newRow;
            });
        }

        const newPosRows = swapSide === "pos" || swapSide === "both" ? swapDayMonth(posRows) : posRows;
        const newFinRows = swapSide === "fin" || swapSide === "both" ? swapDayMonth(finRows) : finRows;

        await handleSmartReconcileWithConfig(config, newPosRows, newFinRows, posName, finName);
    }

    async function handleUpload(data) {
        if (planError === "limit_reached") return;
        setUploadData(data); setScreen("setup"); setLastConfig(null);
    }

    async function handleSmartReconcileWithConfig(config, posRows, finRows, posName, finName) {
        setPlanError(null);
        const { posRowType, finRowType, joinKey, amountCol, industry } = config;

        // Auto-detect: if the join key has duplicates in a file, always aggregate it
        // This means the answer to Q2 is just a hint — actual data always wins
        const keyHasDuplicates = (rows, cols) => {
            const seen = new Set();
            for (const r of rows) {
                const k = cols.map(c => r[c] || "").join("_");
                if (seen.has(k)) return true;
                seen.add(k);
            }
            return false;
        };
        const posHasDupes = keyHasDuplicates(posRows, joinKey.pos);
        const finHasDupes = keyHasDuplicates(finRows, joinKey.fin);

        // User intent: "multiple" (product/other) = detail, "unique" (transaction/day) = summary
        const DETAIL_TYPES = ["product", "other"];
        const userSaysPosDetail = DETAIL_TYPES.includes(posRowType);
        const userSaysFinDetail = DETAIL_TYPES.includes(finRowType);

        // Swap if user says pos=detail, fin=summary (so detail always ends up on fin side)
        let actualPosRows = posRows, actualFinRows = finRows;
        let actualPosName = posName, actualFinName = finName;
        let actualAmountCol = amountCol, actualJoinKey = joinKey;
        let actualPosIsDetail = posHasDupes;
        let actualFinIsDetail = finHasDupes;

        if (userSaysPosDetail && !userSaysFinDetail && posHasDupes && !finHasDupes) {
            // Classic swap: POS is item-level, Finance is order-level
            actualPosRows = finRows; actualFinRows = posRows;
            actualPosName = finName; actualFinName = posName; // eslint-disable-line no-unused-vars
            actualAmountCol = { pos: amountCol.fin, fin: amountCol.pos };
            actualJoinKey   = { pos: joinKey.fin,   fin: joinKey.pos };
            actualPosIsDetail = false;
            actualFinIsDetail = true;
        }

        // Track whether sides were swapped (for future use)
        // const sidesWereSwapped = actualPosName !== posName;

        const buildKey = (row, cols) => cols.map(col => {
            const val = row[col] || "";
            // Normalize date/timestamp columns to YYYY-MM-DD for consistent joining
            const colLower = col.toLowerCase();
            const isDateLike = colLower.includes("date") || colLower.includes("tanggal") || colLower.includes("waktu") || colLower.includes("jam") || colLower.includes("time");
            if (isDateLike && val) return normalizeDate(String(val)) || val;
            return val;
        }).join("_");
        const buildLookup = (rows, cols) => { const l = {}; rows.forEach(r => { const k = buildKey(r, cols); if (!l[k]) l[k] = []; l[k].push(r); }); return l; };
        // eslint-disable-next-line no-unused-vars
        const posDateLookup = buildLookup(actualPosRows, actualJoinKey.pos);
        // eslint-disable-next-line no-unused-vars
        const finDateLookup = buildLookup(actualFinRows, actualJoinKey.fin);

        const aggregateRows = (rows, cols, colName) => {
            const agg = {};
            rows.forEach(r => {
                const k = buildKey(r, cols);
                if (!k) return;
                if (!agg[k]) agg[k] = { ...r, [colName]: 0, _count: 0, _duplicates: [] };
                agg[k][colName] += cleanNumeric(r[colName]);
                agg[k]._count++;
                agg[k]._duplicates.push(r);
            });
            return Object.values(agg);
        };

        const processedPos = actualPosIsDetail ? aggregateRows(actualPosRows, actualJoinKey.pos, actualAmountCol.pos) : actualPosRows;
        const processedFin = actualFinIsDetail ? aggregateRows(actualFinRows, actualJoinKey.fin, actualAmountCol.fin) : actualFinRows;

        // ── Free tier row limit ──────────────────────────────────
        // Check after aggregation — compare the number of unique keys, not raw rows
        const FREE_ROW_LIMIT = 100;
        if (localStorage.getItem("dgb_plan_limited") !== "0") {
            // Only check if not confirmed as paid plan
            try {
                const { data: prof } = await supabase.from("profiles").select("plan").eq("id", user?.id).single();
                if (prof?.plan === "free") {
                    const totalRows = Math.max(processedPos.length, processedFin.length);
                    if (totalRows > FREE_ROW_LIMIT) {
                        setPlanError("row_limit");
                        setScreen("upload");
                        return;
                    }
                }
            } catch (e) { /* non-blocking */ }
        }

        // Detect true duplicates: only meaningful when the file is NOT being aggregated
        // If a file IS being aggregated (detail level), multiple rows per key is expected and normal
        // A true duplicate only exists when a summary-level file unexpectedly has the same key twice
        const detectDuplicateKeys = (rows, cols, isDetail) => {
            if (isDetail) return new Set(); // aggregated files can't have "duplicate" issues
            const counts = {};
            rows.forEach(r => {
                const k = buildKey(r, cols);
                if (!k) return;
                counts[k] = (counts[k] || 0) + 1;
            });
            return new Set(Object.entries(counts).filter(([, c]) => c > 1).map(([k]) => k));
        };

        const posDuplicateKeys = detectDuplicateKeys(actualPosRows, actualJoinKey.pos, actualPosIsDetail);
        const finDuplicateKeys = detectDuplicateKeys(actualFinRows, actualJoinKey.fin, actualFinIsDetail);

        const posMap = {}, finMap = {};
        processedPos.forEach(r => { posMap[buildKey(r, actualJoinKey.pos)] = r; });
        processedFin.forEach(r => { finMap[buildKey(r, actualJoinKey.fin)] = r; });

        const results = [];
        new Set([...Object.keys(posMap), ...Object.keys(finMap)]).forEach(key => {
            const pos = posMap[key], fin = finMap[key];
            const posSales = pos ? cleanNumeric(pos[actualAmountCol.pos]) : null;
            const finSales = fin ? cleanNumeric(fin[actualAmountCol.fin]) : null;
            const salesDiff = (posSales !== null && finSales !== null) ? posSales - finSales : null;

            // Duplicate: same join key appears multiple times in one file, causing inflated total
            const hasTrueDuplicate = salesDiff !== 0 && (
                finDuplicateKeys.has(key) ||
                posDuplicateKeys.has(key)
            );

            let status = "match";
            if (!pos) status = "missing_pos";
            else if (!fin) status = "missing_fin";
            else if (hasTrueDuplicate && (finSales ?? 0) > (posSales ?? 0)) status = "duplicate";
            else if (salesDiff !== 0) status = "mismatch";

            const pjk = actualJoinKey.pos;
            const fjk = actualJoinKey.fin;

            // Build display values from join key columns
            // Prefer pos row; if missing, use equivalent fin join key column
            const joinKeyValues = pjk.map((col, i) => {
                let val = pos?.[col];
                if ((val === undefined || val === null || val === "") && fin) {
                    val = fin[fjk[i]] || fin[col] || "";
                }
                val = String(val || "—");
                const colLower = col.toLowerCase();
                const isDateLike = colLower.includes("date") || colLower.includes("tanggal") || colLower.includes("waktu") || colLower.includes("jam") || colLower.includes("time");
                return isDateLike ? (normalizeDate(val) || val) : val;
            });
            if (Object.keys(posMap).indexOf(key) < 2) {
                // debug removed
            }

            // For backward compat: extract date and storeId from join key values
            const dateIdx = pjk.findIndex(k => k.toLowerCase().includes("date") || k.toLowerCase().includes("tanggal") || k.toLowerCase().includes("waktu") || k.toLowerCase().includes("jam"));
            const dateDisplay = dateIdx >= 0 ? joinKeyValues[dateIdx] : "—";
            const storeIdx = pjk.findIndex(k => !k.toLowerCase().includes("date") && !k.toLowerCase().includes("tanggal") && !k.toLowerCase().includes("waktu") && !k.toLowerCase().includes("jam"));
            const storeDisplay = storeIdx >= 0 ? joinKeyValues[storeIdx] : "—";
            const storeNameDisplay = joinKeyValues.join(" · ");

            const posTxCount = pos ? (pos._count ?? 1) : null;
            const finTxCount = fin ? (fin._count ?? 1) : null;
            results.push({ key, date: dateDisplay, storeId: storeDisplay, storeName: storeNameDisplay, joinKeyValues, joinKeyCols: pjk, posSales, finSales, salesDiff, posTx: posTxCount, finTx: finTxCount, txDiff: (posTxCount && finTxCount) ? posTxCount - finTxCount : null, status, hasDuplicates: hasTrueDuplicate, posRow: pos, finRow: fin });
        });

        const savedConfig = { posRowType, finRowType, joinKey, amountCol, industry };
        handleRun({ results: results.sort((a, b) => String(a.key).localeCompare(String(b.key))), posName, finName, joinKeys: actualJoinKey.pos, industry, lastConfig: { config: savedConfig, posRows, finRows, posName, finName } });
    }

    async function handleSampleRun({ posData, finData, posName, finName }) {
        await handleSmartReconcileWithConfig(
            { posRowType: "transaction", finRowType: "product", joinKey: { pos: ["order_id"], fin: ["order_id"] }, amountCol: { pos: "gross_sales", fin: "gross_sales" } },
            posData.rows, finData.rows, posName, finName
        );
    }

    async function handleSmartReconcile({ posRowType, finRowType, joinKey, amountCol, industry, posFilters, finFilters }) {
        const posRows = applySetupFilters(uploadData.posData.rows, posFilters || []);
        const finRows = applySetupFilters(uploadData.finData.rows, finFilters || []);
        await handleSmartReconcileWithConfig({ posRowType, finRowType, joinKey, amountCol, industry }, posRows, finRows, uploadData.posName, uploadData.finName);
    }

    return (
        <div style={{ background: C.bg, minHeight: "100vh", color: C.text }}>
            <div style={{ borderBottom: "1px solid #222", padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#000000", zIndex: 10 }}>
                <img src={require("../assets/dgb-logo-dark.png")} alt="DGB" style={{ height: 96, cursor: "pointer", marginLeft: "-24px", marginTop: "-8px", marginBottom: "-8px" }} onClick={() => navigate("/")} />
                <button onClick={() => setLang(lang === "en" ? "id" : "en")} style={{ background: "transparent", border: "1.5px solid " + C.border, borderRadius: 6, padding: "0.25rem 0.6rem", fontFamily: MONO, fontSize: "0.72rem", color: C.muted, cursor: "pointer", marginLeft: "0.5rem" }}>
                    {lang === "en" ? "ID 🌐" : "EN 🌐"}
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    {user && <span style={{ fontSize: "0.75rem", color: "#666", fontFamily: MONO }}>{user.email}</span>}
                    {screen === "results" ? (
                        <button onClick={() => setScreen("upload")} style={{ background: "transparent", border: "1.5px solid " + C.border, borderRadius: 6, padding: "0.35rem 1rem", color: "#aaaaaa", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>{t("newReport")}</button>
                    ) : screen === "setup" ? (
                        <button onClick={() => setScreen("upload")} style={{ background: "transparent", border: "1.5px solid " + C.border, borderRadius: 6, padding: "0.35rem 1rem", color: "#aaaaaa", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>{t("backToUpload")}</button>
                    ) : (
                        <button onClick={() => navigate("/")} style={{ background: "transparent", border: "1.5px solid " + C.border, borderRadius: 6, padding: "0.35rem 1rem", color: "#aaaaaa", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>{t("backToHome")}</button>
                    )}
                    {/* Outlier and Segmentation demoted — dimmer color, shorter labels */}
                    <button onClick={() => navigate("/outlier")} style={{ background: "transparent", border: "1.5px solid " + C.border, borderRadius: 6, padding: "0.35rem 1rem", color: C.dim, fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>Outlier</button>
                    <button onClick={() => navigate("/rfm")}     style={{ background: "transparent", border: "1.5px solid " + C.border, borderRadius: 6, padding: "0.35rem 1rem", color: C.dim, fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>Segmentation</button>
                    <button onClick={handleLogout}               style={{ background: "transparent", border: "1.5px solid " + C.border, borderRadius: 6, padding: "0.35rem 1rem", color: "#aaaaaa", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>{t("logOut")}</button>
                </div>
            </div>

            {screen === "upload" && <UploadScreen onRun={handleUpload} onDirectRun={handleSampleRun} userId={user?.id} savedState={savedUpload} onSaveState={setSavedUpload} t={t} planError={planError} planChecking={planChecking} onClearPlanError={() => setPlanError(null)} />}
            {screen === "setup"  && uploadData && <SmartSetup posData={uploadData.posData} finData={uploadData.finData} posName={uploadData.posName} finName={uploadData.finName} onConfirm={handleSmartReconcile} t={t} />}
            {screen === "results" && reportData && <ResultsScreen results={reportData.results} posName={reportData.posName} finName={reportData.finName} joinKeys={reportData.joinKeys} industry={reportData.industry} onRetryDateSwap={handleRetryDateSwap} user={user} t={t} fingerprint={currentFingerprint} savedResolutionData={savedResolutionData} onClearSaved={() => setSavedResolutionData(null)} lastConfig={reportData.lastConfig} />}
        </div>
    );
}
